import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { ArrowRight, ArrowUpRight, Check, ChevronRight, Download, FileText, Link2, RotateCcw } from "lucide-react";
import SeoHead from "../components/SeoHead";
import { ROUTE_SEO } from "../seo/routes";
import { Link } from "react-router-dom";
import { modelName, modelPath } from "../seo/vramModels";
import Seo from "../components/Seo";
import SiteFooter from "../components/SiteFooter";
import "./llm-vram.css";
// Prix du jour : instantané du build puis fichier quotidien du VPS (voir usePrices).
import { priceStamp, usePrices, useLiveData } from "./usePrices";
import { PriceHistory, PriceTable } from "./GpuPriceBlocks";
import { HOURS_PER_MONTH, PRICED_GPUS, gpuPricePath, gpuShort as gpuName0, usd as priceUsd } from "../seo/gpuPrices";
import { REFERRAL_LINKS, rentRel, rentUrl } from "../data/referrals";
import ErrorBoundary from "../components/ErrorBoundary";
import Flag from "../components/Flag";
import ModelPicker from "./ModelPicker";
import { CATALOG_SNAPSHOT, compact, ggufSize, labOf, labOfOrg, licenseInfo, ollamaCommand, prettyModelName, runsWith, strataFastest, strataHow, strataMemory, strataPick, strataWrite, validateCatalog, type CatalogModel } from "./catalog";
import { compareGiB } from "./logParse";
import { reportHtml, type ReportData } from "./vramReport";
import { countryName, type Lab } from "./labs";
import {
  MODELS, CUSTOM_ID, customModel, WEIGHT_FORMATS, KV_FORMATS, RUNTIMES, GPUS,
  compute, minGpus, kvBytesPerSequence, kvBytesPerToken, GiB, parseHfRepo, presetFromHf, formatWarnings, GPU_COUNTS,
  weightBytes, engineBits, qualityHint, modelSheet, offersFor, isNewModel, tidyLabel, servedRepo, presetForRepo, OLLAMA_DEFAULT,
  decodeSpeed, selfHostVsApi, cheapestApi, openRouterPrice, deployCommand, ps5Steps, DEPLOY_ENGINES, type DeployEngine,
  offloadPlan, prefillSeconds, RAM_TYPES, usableMemGiB, RAM_RESERVED_GIB, splitFor, isDatacenter, isUnified, isConsole, weightsPerGpu,
  formatNeeds, runsNatively, runsEngine, kvRuns, adaptFormat, engineFor, singleDevice, isAmdRdna,
  cheapestSetups, formatChoices, fitGrid, modelFit, otherModelsThatFit, setupAfterGpu, setupAfterEngine, setupAfterWeights, setupAfterModel, coherentSetup,
  type ExactSize, type Setup, type Detected, detectGpu, type Electricity, type Gpu, engineReserve, DISPLAY_GIB,
  type Mode, type ModelPreset, type HfApi,
} from "./llmVram";

const HF_ID = "hf";
// Raccourcis du haut de page : un clic charge le modèle et amène au calculateur.
// Dernières sorties, pour la carte « Latest releases » (la veille y ajoute les nouveaux).
const LATEST = MODELS.filter((m) => m.released).sort((a, b) => b.released!.localeCompare(a.released!)).slice(0, 8);
const shortDate = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
/** Poids en 4 bits (Q4_K_M, 4,85 bits) : la taille à comparer à sa mémoire GPU, avant le cache KV. */
const q4GiB = (params: number) => {
  const x = (params * 1e9 * 4.85) / 8 / GiB;
  return params > 200 ? Math.round(x).toLocaleString("en-US") : x.toFixed(1);
};
/** Nom affiché d'un modèle : son libellé de la liste ; importé de Hugging Face, le nom de son
 *  dépôt mis au propre comme dans les tendances (« autotrust/JEV-27B-VL » → « JEV 27B-VL »). */
const labelOf = (m: ModelPreset) =>
  m.id.startsWith("hf:") ? tidyLabel(`${prettyModelName(m.repo.split("/").pop() ?? m.repo)}${m.activeParams ? " (MoE)" : ""}`, m.repo) : m.label;
const nameOf = (m: ModelPreset) => modelName({ ...m, label: labelOf(m) });

/**
 * Une ligne des nouveautés. Toute la ligne est un bouton qui charge le modèle dans le
 * calculateur ; un modèle que le calculateur ne sait pas charger devient un lien externe
 * (`href`, sa page Ollama), signalé par une autre flèche.
 */
function NewsRow({ name, meta, isNew, lab, onClick, href, rank }: {
  name: string; meta: ReactNode; isNew?: boolean; lab?: Lab; onClick?: () => void; href?: string; rank?: number;
}) {
  const body = (
    <>
      {/* Rang d'un classement : la liste numérotée (<ol>) le donne déjà aux lecteurs d'écran. */}
      {rank && <span className="vram-news-rank" aria-hidden="true">{rank}</span>}
      <span className="vram-news-main">
        <span className="vram-news-name">
          <Flag country={lab?.country} label={lab?.country && countryName(lab.country)} />{name}{isNew && <span className="vram-new">New</span>}
        </span>
        <span className="vram-news-meta">{meta}</span>
      </span>
      {href ? <ArrowUpRight className="vram-news-go" size={18} aria-hidden="true" /> : <ChevronRight className="vram-news-go" size={18} aria-hidden="true" />}
      <span className="sr-only">{href ? " (opens in a new tab)" : ", size it in the calculator"}</span>
    </>
  );
  return (
    <li>
      {href
        ? <a className="vram-news-row" href={href} target="_blank" rel="noopener noreferrer">{body}</a>
        : <button type="button" className="vram-news-row" onClick={onClick}>{body}</button>}
    </li>
  );
}

/** Lit config.json + les métadonnées safetensors d'un repo Hugging Face (CORS ouvert). Un dépôt de fichiers GGUF
 *  seuls (TheBloke/Llama-2-7B-GGUF : pas de safetensors, un config.json réduit à model_type) n'a pas l'architecture :
 *  son modèle d'origine (base_model de sa fiche) est lu à sa place, une seule fois. */
async function fetchHfModel(repo: string, viaGguf = false): Promise<ModelPreset> {
  let cfg: Response, api: Response;
  try {
    [cfg, api] = await Promise.all([
      fetch(`https://huggingface.co/${repo}/resolve/main/config.json`),
      fetch(`https://huggingface.co/api/models/${repo}?blobs=true`),
    ]);
  } catch {
    throw new Error("Could not reach Hugging Face. Check your connection and try again.");
  }
  // Hugging Face répond 401 aussi bien pour un repo inexistant que pour un modèle « gated ».
  if (cfg.status === 401 || cfg.status === 403) {
    throw new Error(viaGguf
      ? `This GGUF repo is based on ${repo}, which is gated or private: paste an ungated copy of it, such as unsloth/….`
      : "Model not found, private, or gated (Hugging Face only serves gated configs to signed-in users). For gated models, try an ungated copy such as unsloth/….");
  }
  if (cfg.status === 429) throw new Error("Hugging Face is rate-limiting your network. Wait a minute and try again.");
  const info = (api.ok ? await api.json() : {}) as HfApi & { cardData?: { base_model?: unknown } };
  const files = (info.siblings ?? []).map((f) => f.rfilename);
  const ggufOnly = files.some((f) => f.endsWith(".gguf")) && !files.some((f) => f.endsWith(".safetensors"));
  if (ggufOnly || cfg.status === 404) {
    const base = [info.cardData?.base_model].flat().find((b): b is string => typeof b === "string" && /^[\w.-]+\/[\w.-]+$/.test(b));
    if (base && !viaGguf && base.toLowerCase() !== repo.toLowerCase()) return fetchHfModel(base, true);
    throw new Error(ggufOnly
      ? "This repo only holds GGUF files, without the model's architecture: paste the original model instead (the one it was converted from)."
      : "This repo has no config.json: paste the original model instead.");
  }
  if (!cfg.ok) throw new Error(`Hugging Face answered ${cfg.status}. Try again later.`);
  return presetFromHf(repo, await cfg.json(), info);
}

// ponytail: anti-rafale côté navigateur seulement. Les requêtes partent du navigateur
// du visiteur vers Hugging Face, jamais par notre serveur : rien à protéger sur le VPS,
// et HF limite déjà par IP. Ici on évite juste de refaire le même appel et les clics en
// rafale. Une vraie limite (contournable autrement) n'aurait de sens qu'avec un proxy.
const HF_COOLDOWN_MS = 3000;
const hfCache = new Map<string, Promise<ModelPreset>>();
let nextHfFetch = 0;

function loadHfModel(repo: string): Promise<ModelPreset> {
  const key = repo.toLowerCase();
  const cached = hfCache.get(key);
  if (cached) return cached;
  // Pas de refus : on attend simplement la fin du délai (le bouton reste sur « Loading… »).
  const wait = Math.max(0, nextHfFetch - Date.now());
  nextHfFetch = Date.now() + wait + HF_COOLDOWN_MS;
  const p = new Promise((r) => setTimeout(r, wait)).then(() => fetchHfModel(repo));
  hfCache.set(key, p);
  p.catch(() => hfCache.delete(key)); // un échec peut se retenter
  return p;
}

// Toute la physique est dans llmVram.ts (vérifiée par `npm run check:vram`) ;
// ce fichier n'est que l'interface.

const gib = (x: number) => `${x >= 100 ? Math.round(x).toLocaleString("en-US") : x >= 10 ? x.toFixed(1) : x.toFixed(2)} GiB`;
const MCP_URL = "https://studiotvai.com/api/mcp";
const MCP_JSON = { name: "studiotv-vram", type: "http", url: MCP_URL };
/** Clients MCP : où mettre l'adresse, ce qu'on copie, et un lien d'installation en un clic quand le client
 *  en a un (Cursor : configuration en base64, docs « MCP install links » ; VS Code : JSON encodé dans
 *  vscode:mcp/install). Procédures vérifiées le 14 octobre 2026. */
const MCP_CLIENTS: { id: string; label: string; how: string; code: string; install?: { label: string; href: string } }[] = [
  { id: "claude", label: "Claude", how: "Claude (web or desktop): Settings → Connectors → Add custom connector, paste this URL, then turn it on in a chat with the + button.", code: MCP_URL },
  { id: "claude-code", label: "Claude Code", how: "In a terminal:", code: `claude mcp add --transport http studiotv-vram ${MCP_URL}` },
  {
    id: "cursor", label: "Cursor", how: "One click, or add this to ~/.cursor/mcp.json:",
    code: JSON.stringify({ mcpServers: { "studiotv-vram": { url: MCP_URL } } }, null, 2),
    install: { label: "Add to Cursor", href: `cursor://anysphere.cursor-deeplink/mcp/install?name=studiotv-vram&config=${btoa(JSON.stringify({ url: MCP_URL }))}` },
  },
  {
    id: "vscode", label: "VS Code", how: "One click (GitHub Copilot, agent mode), or in a terminal:",
    code: `code --add-mcp '${JSON.stringify(MCP_JSON)}'`,
    install: { label: "Add to VS Code", href: `vscode:mcp/install?${encodeURIComponent(JSON.stringify(MCP_JSON))}` },
  },
  { id: "chatgpt", label: "ChatGPT", how: "ChatGPT (paid plans): Settings → Apps → Advanced settings → Developer mode on, then Create: paste this URL, no authentication.", code: MCP_URL },
  { id: "gemini", label: "Gemini CLI", how: "In a terminal:", code: `gemini mcp add --transport http studiotv-vram ${MCP_URL}` },
  { id: "other", label: "Other", how: "Any MCP client that speaks Streamable HTTP. No key, no sign-up.", code: `${MCP_URL}\nTools: estimate_vram, models_that_fit, gpu_prices` },
];
const OLLAMA_FORMATS: Record<string, string> = { Q8_0: "q8_0", Q6_K: "q6_k", Q5_K_M: "q5_k_m", Q4_K_M: "q4_k_m", Q3_K_M: "q3_k_m", Q2_K: "q2_k", F16: "bf16", BF16: "bf16" };
/** « 2026-05-17 » → « May 17, 2026 » */
const longDay = (day: string) => new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });

function bytes(b: number) {
  if (b >= GiB) return gib(b / GiB);
  if (b >= 2 ** 20) return `${(b / 2 ** 20).toFixed(1)} MiB`;
  return `${(b / 1024).toFixed(1)} KiB`;
}

function tokens(n: number) {
  if (n >= 1 << 20 && n % (1 << 20) === 0) return `${n / (1 << 20)}M`;
  if (n >= 1024 && n % 1024 === 0) return `${n / 1024}K`;
  return n.toLocaleString("en-US");
}

const tps = (x: number) => (x < 10 ? x.toFixed(1) : Math.round(x).toLocaleString("en-US"));
const secs = (x: number) => (x < 1 ? `${Math.max(1, Math.round(x * 1000))} ms` : x < 90 ? `${x.toFixed(x < 10 ? 1 : 0)} s` : `${(x / 60).toFixed(1)} min`);
// Séparateurs de milliers au-delà de 1 000 (« 729,600.00 », pas « 729600.00 »), comme les pages de prix.
const usd = (x: number) => (x === 0 ? "0.00" : x < 0.01 ? x.toFixed(4) : x < 1 ? x.toFixed(3) : x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
/** Prix de location à l'heure ; « total » dès que plusieurs GPU sont comptés, pour ne pas le
 *  confondre avec le prix par GPU du tableau des loueurs. */
/** Dollars à deux décimales, avec séparateur de milliers (« $1,540.10 », pas « $1540.10 »). */
const usd2 = (x: number) => x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Milliards de paramètres : deux décimales sous 10, une au-delà, avec séparateur (« 2,779.9 »). */
const billions = (p: number) => (p < 10 ? p.toFixed(2) : p.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const perHour = (x: number, gpus: number) => `$${usd2(x)}/h${gpus > 1 ? " total" : ""}`;
const bigCount = (x: number) => (x >= 1e9 ? `${(x / 1e9).toFixed(1)}B` : x >= 1e6 ? `${(x / 1e6).toFixed(1)}M` : `${Math.round(x / 1e3)}K`);

const num = (s: string) => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};
// Saisie bornée : un champ vide, négatif ou absurde ne doit jamais casser le calcul.
const bound = (s: string, lo: number, hi: number) => Math.min(hi, Math.max(lo, num(s)));

/** Défile jusqu'à une section et y place le focus : sans ça, au clavier, la touche Tab
 *  repartait de l'endroit d'où l'on vient. Défilement instantané si l'utilisateur
 *  a demandé moins d'animations. Les blocs des étapes jamais affichés comptent 800 px estimés
 *  (content-visibility, llm-vram.css) et prenaient leur vraie hauteur pendant qu'on les traversait :
 *  la cible remontait et la page s'arrêtait plus bas (« Compare 4 providers » tombait sur « Deploy
 *  it », 1 600 px trop bas sur mobile). Ils sont donc dessinés avant le calcul de la position ; leur
 *  hauteur est ensuite retenue (contain-intrinsic-size: auto). */
function jumpTo(id: string) {
  const target = document.getElementById(id);
  if (!target) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const root = document.documentElement;
  root.classList.add("vram-jumping");
  target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  target.focus({ preventScroll: true });
  const done = () => root.classList.remove("vram-jumping");
  window.addEventListener("scrollend", done, { once: true });
  setTimeout(done, 2000); // pas de scrollend (déjà sur place, ou navigateur qui ne l'émet pas)
}


/** Partie de l'écran où un élément est visible : la fenêtre, réduite par chaque ancêtre qui coupe ce
 *  qui dépasse (le cadre du calculateur est en overflow: clip), à 8 px des bords. */
function visibleBox(el: HTMLElement) {
  const box = { left: 8, top: 8, right: innerWidth - 8, bottom: innerHeight - 8 };
  for (let a = el.parentElement; a; a = a.parentElement) {
    const s = getComputedStyle(a);
    if (s.overflowX === "visible" && s.overflowY === "visible") continue;
    const r = a.getBoundingClientRect();
    if (s.overflowX !== "visible") { box.left = Math.max(box.left, r.left + 8); box.right = Math.min(box.right, r.right - 8); }
    if (s.overflowY !== "visible") { box.top = Math.max(box.top, r.top + 8); box.bottom = Math.min(box.bottom, r.bottom - 8); }
  }
  return box;
}

/** Bulle « i ». title= ne s'affiche ni au tap (mobile) ni au clavier : bouton qui ouvre
 *  la bulle au clic, au tap ou à la touche Entrée, et au survol sur ordinateur (CSS).
 *  À l'ouverture, elle se place dans la partie visible : décalée vers la gauche près du bord droit
 *  (« i » en fin de ligne, sinon coupée par le cadre du calculateur), au-dessus de la pastille s'il
 *  n'y a pas la place dessous. Sur mobile, elle est déjà fixe, pleine largeur (CSS). */
function Info({ text, label }: { text: string; label: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const id = useId();
  const place = () => {
    const tip = tipRef.current, wrap = ref.current;
    if (!tip || !wrap || getComputedStyle(tip).position === "fixed") return;
    tip.style.removeProperty("--tip-x");
    tip.classList.remove("up");
    const r = tip.getBoundingClientRect();
    if (!r.width) return; // pas affichée
    const box = visibleBox(tip), anchor = wrap.getBoundingClientRect();
    const x = r.right > box.right ? box.right - r.right : r.left < box.left ? box.left - r.left : 0;
    if (x) tip.style.setProperty("--tip-x", `${Math.round(Math.max(x, box.left - r.left))}px`);
    if (r.bottom > box.bottom && anchor.top - 8 - r.height >= box.top) tip.classList.add("up");
  };
  useEffect(() => {
    if (open) place();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    // iOS ne donne pas le focus aux boutons : onBlur ne suffit pas pour fermer au tap ailleurs.
    const outside = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const escape = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);
  return (
    <span className="vram-info-wrap" ref={ref} onMouseEnter={() => requestAnimationFrame(place)}>
      <button
        type="button" className="vram-info" aria-label={`More info: ${label}`} aria-describedby={id} aria-expanded={open}
        onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }} onBlur={() => setOpen(false)}
      >i</button>
      <span id={id} ref={tipRef} role="tooltip" className={`vram-tip${open ? " open" : ""}`}>{text}</span>
    </span>
  );
}

function Field({ id, label, info, children }: { id: string; label: string; info?: string; children: ReactNode }) {
  return (
    <div className="vram-control">
      {/* Bouton « i » à côté du label, pas dedans : un label ne contient que son champ. */}
      <div className="vram-label">
        <label htmlFor={id}>{label}</label>
        {info && <Info text={info} label={label} />}
      </div>
      {children}
    </div>
  );
}

/** used : la valeur que le calcul utilise vraiment (bornée). En quittant le champ, une saisie
 *  vide, négative ou hors limites est remplacée par elle : ce qui est affiché est ce qui est calculé. */
function NumberInput({ id, value, onChange, min = 0, step, used }: {
  id: string; value: string; onChange: (v: string) => void; min?: number; step?: number; used: number;
}) {
  return (
    <input
      id={id} type="number" inputMode="decimal" min={min} step={step}
      className="vram-input" value={value} onChange={(e) => onChange(e.target.value)}
      onBlur={() => { if (value.trim() === "" || num(value) !== used) onChange(String(used)); }}
    />
  );
}

/** Texte de la simulation, découpé en morceaux de la taille d'un token (environ 4 caractères) : 250 tokens environ,
 *  pour qu'une configuration rapide reste visible quelques secondes au lieu de finir d'un coup. */
const PREVIEW_TOKENS = ("Sure. To run a model on your own GPU, its weights must fit in memory along with the KV cache. Speed then " +
  "depends on memory bandwidth: every new token reads the active weights again, so a GPU with twice the bandwidth " +
  "writes about twice as fast. The first token comes later than the others, because the whole prompt has to be read " +
  "first; that part depends on compute, not on bandwidth. Serving several users at once raises the total throughput, " +
  "but each user then sees a slower stream. A quantized model reads fewer bytes per token, which is why 4-bit weights " +
  "often run faster than 16-bit ones on the same card. Mixture-of-experts models only read the experts picked for each " +
  "token, so they write much faster than a dense model of the same total size.").match(/\s*\S{1,4}/g) ?? [];
/** Vitesse de lecture silencieuse d'un adulte : 238 mots par minute (Brysbaert, 2019), soit ~5 tokens/s (0,75 mot par token). */
const READING_TPS = 238 / 60 / 0.75;

/** Un texte qui s'écrit à la vitesse estimée, face à l'API la plus rapide du modèle (ttft : secondes avant le
 *  premier token, affichées ; l'animation en attend 3 au plus). Démarre quand il entre à l'écran. Le texte complet, invisible, réserve la place : rien ne bouge
 *  autour ; si le visiteur a demandé moins d'animations, c'est lui qui s'affiche, immobile (CSS). */
function SpeedPreview({ lanes }: { lanes: { label: string; tps: number; ttft: number }[] }) {
  const [t0, setT0] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const wait = (l: { ttft: number }) => Math.min(3, l.ttft);
  const total = Math.max(...lanes.map((l) => wait(l) + PREVIEW_TOKENS.length / Math.max(0.1, l.tps)));
  useEffect(() => {
    const el = ref.current;
    if (!el || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setT0(performance.now()); io.disconnect(); } }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (t0 === null) return;
    let raf = 0;
    const tick = () => { const n = performance.now(); setNow(n); if ((n - t0) / 1000 < total + 0.2) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [t0, total]);
  const elapsed = t0 === null ? 0 : (now - t0) / 1000;
  return (
    <div className="vram-preview" ref={ref}>
      <p className="sr-only">
        {lanes.map((l) => `${l.label}: about ${tps(l.tps)} tokens per second, first token after ${secs(l.ttft)}`).join("; ")}.
      </p>
      <div className={`vram-preview-lanes${lanes.length > 1 ? " two" : ""}`} aria-hidden="true">
        {lanes.map((l) => {
          const shown = Math.max(0, Math.min(PREVIEW_TOKENS.length, Math.floor((elapsed - wait(l)) * l.tps)));
          const done = shown >= PREVIEW_TOKENS.length;
          return (
            <div key={l.label} className="vram-preview-lane">
              <div className="vram-preview-head">
                {l.label} <b>{tps(l.tps)} tok/s</b> · first token {secs(l.ttft)}
                <span className="vram-preview-count">{shown} / {PREVIEW_TOKENS.length} tokens</span>
              </div>
              <div className="vram-preview-stack">
                <p className="vram-preview-text vram-preview-ghost">{PREVIEW_TOKENS.join("")}</p>
                <p className="vram-preview-text vram-preview-live">{PREVIEW_TOKENS.slice(0, shown).join("")}{!done && <span className="vram-caret" />}</p>
              </div>
            </div>
          );
        })}
      </div>
      <button type="button" className="vram-share vram-preview-replay" onClick={() => setT0(performance.now())}>
        {t0 !== null && elapsed < total ? "Restart" : "Replay"}
      </button>
    </div>
  );
}

/** Lit le nom du GPU dans ce navigateur (WebGL) ; null s'il est masqué ou si WebGL manque. */
function readRenderer(): string | null {
  try {
    const gl = document.createElement("canvas").getContext("webgl");
    if (!gl) return null;
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const value = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    gl.getExtension("WEBGL_lose_context")?.loseContext(); // libère le contexte tout de suite
    return typeof value === "string" && value && !/^(WebKit WebGL|Mozilla|Apple GPU|Generic Renderer)$/i.test(value) ? value : null;
  } catch {
    return null;
  }
}

const noSubscribe = () => () => {};
/** Pays du visiteur d'après la langue du navigateur (« fr » → FR, « en-GB » → GB) ; null sans région connue. */
function localeRegion(): string | null {
  try {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const region = new Intl.Locale(tag).maximize().region;
      if (region) return region;
    }
  } catch { /* Intl.Locale absent : pas de pays par défaut */ }
  return null;
}

/** Faire tourner le GPU chez soi : l'électricité à pleine puissance (borne haute), par heure, par mois et par
 *  million de tokens, face à la location. Prix des ménages d'Eurostat par pays, ou celui du visiteur. */
function HomePower({ gpu, gpus, power, rent, tokPerSec }: {
  gpu: Gpu; gpus: number; power: Electricity; rent: { name: string; price: number } | null; tokPerSec: number | null;
}) {
  const countries = useMemo(() => Object.entries(power.countries).sort((a, b) => a[1].name.localeCompare(b[1].name)), [power]);
  const priceOf = (code: string) => (power.countries[code] ? (power.countries[code].eur * power.eurUsd).toFixed(3) : "");
  // Rendu serveur : aucun pays (rien d'arbitraire) ; dans le navigateur, celui de sa langue s'il est connu.
  const region = useSyncExternalStore(noSubscribe, localeRegion, () => null);
  const [picked, setPicked] = useState<string | null>(null);
  const [typed, setTyped] = useState<string | null>(null);
  const country = picked ?? (region && power.countries[region] ? region : "custom");
  const kwh = typed ?? priceOf(country);
  const [hours, setHours] = useState("8");
  const defaultWatts = gpu.watts ? String(gpu.watts * gpus) : "";
  const [watts, setWatts] = useState<{ key: string; v: string } | null>(null);
  const wattsKey = `${gpu.id}|${gpus}`;
  const wattsStr = watts?.key === wattsKey ? watts.v : defaultWatts;
  const price = num(kwh), w = num(wattsStr), h = Math.min(24, Math.max(0, num(hours) || 0));
  const perHour = price > 0 && w > 0 ? (w / 1000) * price : null;
  const c = power.countries[country];
  const [y, half] = power.period.split("-S");
  return (
    <section className="vram-results-panel vram-home" aria-labelledby="vram-home-title">
      <h3 className="vram-block-title vram-panel-title" id="vram-home-title">Run it at home: electricity</h3>
      <div className="vram-field-grid vram-home-fields">
        <Field id="home-country" label="Country">
          <select id="home-country" className="vram-select" value={country}
            onChange={(e) => { setPicked(e.target.value); setTyped(e.target.value === "custom" ? "" : null); }}>
            <option value="custom">Other: enter your price</option>
            {countries.map(([code, x]) => <option key={code} value={code}>{x.name}</option>)}
          </select>
        </Field>
        <Field id="home-kwh" label="Price per kWh ($)" info={c ? `${c.name}: €${c.eur} per kWh, all taxes included (Eurostat), at the ECB rate of ${power.eurUsd} $ per €` : "Your electricity price, in dollars per kWh, as on your bill"}>
          <input id="home-kwh" type="number" inputMode="decimal" min={0} step={0.01} className="vram-input" placeholder="Your price"
            value={kwh} onChange={(e) => { setTyped(e.target.value); setPicked("custom"); }} />
        </Field>
        <Field id="home-watts" label={`Power, ${gpus > 1 ? `${gpus} GPUs` : "GPU"} (W)`}
          info={isConsole(gpu) ? `${gpu.watts} W: the console's chip while it generates, as PS5LM Plus reads it. The whole console draws a little more at the wall.`
            : gpu.watts ? `${gpu.watts} W per card at full load (manufacturer's board power${gpu.group === "Laptop GPUs" ? ", maximum TGP with Dynamic Boost" : ""})` : "Apple does not publish the GPU's power: sudo powermetrics shows it while a model runs"}>
          <input id="home-watts" type="number" inputMode="numeric" min={0} step={10} className="vram-input" placeholder="Watts"
            value={wattsStr} onChange={(e) => setWatts({ key: wattsKey, v: e.target.value })} />
        </Field>
        <Field id="home-hours" label="Hours a day">
          <input id="home-hours" type="number" inputMode="decimal" min={0} max={24} step={1} className="vram-input"
            value={hours} onChange={(e) => setHours(e.target.value)} />
        </Field>
      </div>
      {perHour !== null ? (
        <>
          <div className="vram-metrics-grid vram-metrics-3">
            <Metric primary label="Per hour" value={`$${usd(perHour)}`} />
            <Metric label={`Per month, ${h} h a day`} value={`$${usd(perHour * h * 30.4)}`} />
            {tokPerSec ? (
              <Metric label="Per 1M tokens" value={`$${usd((perHour / (tokPerSec * 3600)) * 1e6)}`}
                info={`At full load: ${tps(tokPerSec)} tok/s in total, as estimated above`} />
            ) : null}
          </div>
          {rent && (
            <p className="vram-note">
              Renting the same {gpus > 1 ? `${gpus} GPUs` : "GPU"} costs <b>${usd2(rent.price)}/h</b> on {rent.name}, {rent.price / perHour >= 1.5 ? `${Math.round(rent.price / perHour)}×` : "about as much as"} the electricity
              at home. The card itself is not counted.
            </p>
          )}
        </>
      ) : (
        <p className="vram-note">{!(w > 0) ? "Enter the power in watts to see the cost." : "Choose your country or enter your price per kWh."}</p>
      )}
      <p className="vram-footnote">
        Upper bound: board power at full load; generation usually draws less, the rest of the computer adds some. Household prices,
        all taxes included, 2,500 to 4,999 kWh a year: Eurostat, {half === "1" ? "first" : "second"} half of {y} (no figure for the US or the UK: enter yours).
      </p>
    </section>
  );
}

function Metric({ label, value, primary, info }: { label: string; value: string; primary?: boolean; info?: string }) {
  return (
    <div className={`vram-metric${primary ? " primary" : ""}`}>
      <span>{label}{info && <> <Info text={info} label={label} /></>}</span>
      <strong>{value}</strong>
    </div>
  );
}

/** En-tête d'une étape des résultats (réponse, options, location) : numéro, titre, une phrase. */
/** Sur mobile, les listes de l'étape 3 montrent leurs MOBILE_ITEMS premiers éléments (plus
 *  l'élément choisi) : ce bouton affiche le reste. Sur ordinateur, tout est visible et il est masqué. */
const MOBILE_ITEMS = 4;
const mobileExtra = (i: number, active: boolean) => (i >= MOBILE_ITEMS && !active ? " vram-extra" : "");

function ShowAll({ count, open, onOpen }: { count: number; open: boolean; onOpen: () => void }) {
  if (open || count <= MOBILE_ITEMS) return null;
  return <button type="button" className="vram-share vram-show-all" onClick={onOpen}>Show all {count}</button>;
}

function TierHead({ n, title, desc }: { n: number; title: string; desc: string }) {
  return (
    <header className="vram-tier-head">
      <span className="vram-tier-num" aria-hidden="true">{n}</span>
      <div>
        <h2 className="vram-tier-title">{title}</h2>
        <p className="vram-tier-desc">{desc}</p>
      </div>
    </header>
  );
}

// Une seule source pour la FAQ visible et le JSON-LD : Google exige qu'ils concordent.
// Chiffres des FAQ « 70B » et « 24 GB » : calculés par le moteur, une fois au chargement.
const fmt = (x: number) => (x >= 100 ? Math.round(x).toLocaleString("en-US") : x.toFixed(1));
const llama70 = MODELS.find((m) => m.id === "llama-3.3-70b")!;
const s70 = modelSheet(llama70);
const w70 = (id: string) => fmt(s70.formats.find((f) => f.id === id)!.weightsGiB);
const n70 = (gpu: string, id: string) => s70.gpus.find((g) => g.id === gpu)!.counts[s70.formats.findIndex((f) => f.id === id)];
const fits24 = MODELS.filter((m) => {
  const sh = modelSheet(m);
  return sh.gpus.find((g) => g.id === "rtx-4090")!.counts[sh.formats.findIndex((f) => f.id === "q4_k_m")] === 1;
}).map(modelName);

// Tableau « VRAM requirements by model » : poids seuls, par précision.
const MODEL_TABLE = MODELS.map((m) => ({
  m,
  lab: labOf(m),
  bf16: weightBytes(m, 16) / GiB,
  fp8: weightBytes(m, 8) / GiB,
  q4: weightBytes(m, 4.85) / GiB,
}));

/** points : liste affichée sous la réponse ; chaque point commence par son terme en gras,
 *  et le texte complet (réponse + points) part tel quel dans le JSON-LD. */
type FaqItem = { q: string; a: string; points?: [string, string][]; id?: string };
const faqText = ({ a, points }: FaqItem) => (points ? [a, ...points.map(([b, t]) => `${b} ${t}`)].join(" ") : a);

const ACCURACY = CATALOG_SNAPSHOT.accuracy;

const FAQ: FaqItem[] = [
  {
    id: "faq-method",
    q: "How does the calculator compute VRAM?",
    a: "It adds up what each GPU must hold, for the engine and the GPU you pick, and checks that it fits in the memory the engine allows itself.",
    points: [
      ["Weights:", "parameters × bits ÷ 8, split across the tensor-parallel and pipeline-parallel GPUs. Official checkpoints and published GGUF files count at their exact file size."],
      ["KV cache:", "for each group of layers, layers × width × cached tokens × bytes, times the concurrent requests. Width is 2 × KV heads × head dim (GQA), or one compressed latent plus its RoPE part (MLA)."],
      ["Cached tokens:", "the whole context on full-attention layers, at most the window on sliding-window layers (a modern engine with a paged KV cache keeps only that), the context ÷ the ratio on compressed layers."],
      ["Engine reserve:", "what the engine keeps for itself on that GPU: vLLM's 90% default and its overhead measured on real server logs, SGLang's own mem_fraction_static formula, llama.cpp's 1 GiB --fit margin. (KV cache + state) × requests + weights + overhead must stay under memory × usable %."],
    ],
  },
  {
    id: "faq-accuracy",
    q: "How accurate is the calculator?",
    a: (ACCURACY
      ? `It is checked every day against real server launches published by InferenceX (SemiAnalysis): across ${ACCURACY.runs} launches, the median gap is ${ACCURACY.weightsPct}% on weights per GPU, ${ACCURACY.kvPct}% on the KV cache allocated and ${ACCURACY.speedPct}% on speed per request. `
      : "It is checked every day against real server launches published by InferenceX (SemiAnalysis). ") +
      "Memory follows each architecture exactly; speed is an estimate from memory bandwidth (±30–50%), and training activations a ±30% estimate. Keep some headroom and confirm with nvidia-smi.",
  },
  {
    q: "How much VRAM do I need to run a 70B model?",
    a: `Take Llama 3.3 70B. Its weights need ${w70("bf16")} GiB in BF16 (${n70("h100", "bf16")} × H100 80 GB), ${w70("fp8")} GiB in FP8 and about ${w70("q4_k_m")} GiB in 4-bit (${n70("h100", "q4_k_m")} × H100, or ${n70("rtx-4090", "q4_k_m")} × RTX 4090). Then add the KV cache of each request: ${fmt(s70.kv[0].bf16GiB)} GiB at 8K tokens and ${fmt(s70.kv[1].bf16GiB)} GiB at 32K.`,
  },
  {
    q: "Can I run an LLM on a 24 GB GPU like the RTX 4090?",
    a: `Yes, models up to about 35B parameters fit in 4-bit with an 8K-token context: ${fits24.join(", ")}. Larger models need several GPUs or a card with more memory.`,
  },
  {
    q: "How many GPUs do I need to run an LLM?",
    a: "Each GPU must hold its share of the weights plus the KV cache of every concurrent request, under the memory the serving engine allows itself (90% by default in vLLM). The calculator tries 1, 2, 4, 8 GPUs and more, splitting the model with tensor parallelism inside a server and pipeline parallelism across servers, and returns the fewest that fit.",
  },
  {
    q: "What drives LLM VRAM usage?",
    a: "Four things decide how much memory a model needs once it is serving requests.",
    points: [
      ["Weights", "are fixed: parameters × bits. “4-bit” formats really cost 4.2 to 4.9 bits once scales are counted."],
      ["KV cache", "grows with context × concurrent sequences; it decides how many users a GPU can serve."],
      ["Tensor parallelism", "splits GQA heads but never below one head per GPU, and replicates MLA latents."],
      ["Serving engines", "leave memory free on purpose: vLLM uses 90% of each GPU by default."],
    ],
  },
  {
    q: "Why is VRAM higher than the model size on disk?",
    a: "The file holds only the weights. At serving time you also pay for the KV cache, which grows with context × concurrent sequences, runtime buffers such as CUDA graphs and activation workspace, and the share of memory the engine leaves free on purpose.",
  },
  {
    q: "Why are most LLM VRAM formulas wrong?",
    a: "The classic estimate, 2 × layers × KV heads × head dim × bytes per token, assumes every layer attends to the whole context. Most models released since 2025 break that rule:",
    points: [
      ["Hybrid linear attention", "(Qwen 3.5–3.8, Nemotron 3, Kimi K3): only 1 layer in 4, or fewer, keeps a KV cache; the rest hold a fixed-size state."],
      ["Sliding windows", "(Gemma 4, gpt-oss): most layers keep only the last 128 to 1,024 tokens."],
      ["MLA", "(GLM-5.3, Kimi K3): one 576-wide compressed latent per token instead of keys and values."],
      ["Compressed attention", "(DeepSeek V4): a 128-token window plus the rest of the sequence compressed 4× or 128×."],
    ],
  },
  {
    q: "Why does the KV cache differ so much between models of the same size?",
    a: "2026 architectures cache very different amounts per token. Qwen 3.5+ keeps a KV cache on only 1 layer in 4, Gemma 4 and gpt-oss limit most layers to a short sliding window, MLA models store one compressed latent instead of keys and values, and DeepSeek V4 compresses the sequence itself. At 128K context, Llama 3.3 70B needs 40 GiB of KV per sequence; Qwen3.8 27B needs 8 GiB.",
  },
  {
    q: "Can quantization make a model fit on fewer GPUs?",
    a: "Yes. Weights take parameters × bits per weight, so going from BF16 to 8 bits halves them and 4-bit formats cut them by about 3.5×. As a rule of thumb, 8-bit is near lossless, 5 to 6-bit loses very little and 4-bit loses a little more, depending on the model. Set a fixed number of GPUs and the calculator shows the most accurate format that fits.",
  },
  {
    q: "Does quantizing the weights shrink the KV cache?",
    a: "No. The weight format (FP8, NVFP4, GGUF Q4_K_M…) and the KV cache precision are set independently. An FP8 KV cache halves the cache whatever the weights are stored in, at a small accuracy cost.",
  },
  {
    q: "How much memory does fine-tuning take?",
    id: "faq-training",
    a: "Full fine-tuning with AdamW in mixed precision costs about 16 bytes per parameter before activations: BF16 weights and gradients, an FP32 master copy and two FP32 optimizer moments. LoRA keeps the frozen model at 2 bytes per parameter plus 16 per trainable one, QLoRA at about 0.52. Activations then scale with sequence length × batch, not with parameter count: about layers × 34 × sequence × batch × hidden bytes, or layers × 2 × sequence × batch × hidden plus one layer with gradient checkpointing, plus FP32 logits of sequence × batch × vocabulary × 4 bytes. They are a ±30% estimate: they depend on the MLP shape, fused kernels and the framework, and a fused cross-entropy (Liger) removes most of the logits term.",
  },
];

const MODE_LABEL: Record<Mode, string> = { inference: "Inference", full: "Full fine-tune", lora: "LoRA", qlora: "QLoRA" };
const STATUS_LABEL = { ok: "Fits", tight: "Tight", oom: "Does not fit", ram: "Fits with RAM" };
const modelGroups = [...new Set(MODELS.map((m) => m.group))];
const gpuGroups = [...new Set(GPUS.map((g) => g.group))];

// Valeurs des paramètres omis d'un lien partagé (et réglages de départ). gpt-oss 120B : le
// modèle par défaut d'avant les tendances, gardé pour que les anciens liens n'en changent pas.
const DEFAULTS = {
  model: "gpt-oss-120b", c: "14,40,8,128,5120,131072", mode: "inference", w: "native", kv: "bf16",
  ctx: "32768", req: "4", gpu: "h100", mem: "80", n: "auto", tp: "1", pp: "1",
  engine: "vllm", util: "", oh: "", disp: "0", lora: "1", gc: "1", shard: "1", ram: "64", ramt: "ddr5-2",
};
const CUSTOM_START = { params: "14", layers: "40", kvHeads: "8", headDim: "128", hidden: "5120", vocab: "131072" };
/** Longueur de séquence par défaut à l'entraînement. Les 32K de l'inférence, × 4 séquences par GPU, donnaient
 *  « plus de 128 H100 » pour un LoRA de Llama 3.1 8B (62 Gio de logits FP32 à eux seuls). */
const TRAINING_CTX = "4096";
const isNum = (v: string) => /^\d{1,9}(\.\d{1,4})?$/.test(v);
/** Lien partagé : une mémoire, un contexte ou un nombre de requêtes nul n'a pas de sens ; une part de mémoire va de
 *  1 à 100 % (sinon « 0 % » donnait « plus de 128 GPU »). */
const isPos = (v: string) => isNum(v) && Number(v) > 0;
const isPct = (v: string) => isNum(v) && Number(v) >= 1 && Number(v) <= 100;
/** Mémoire utilisable (%) et réserve (GB) d'un moteur, telles que les champs les affichent. */
// Part utilisable et réserve : vides, celles que le moteur règle lui-même sur ce GPU (reserve.ts) ; une valeur
// saisie (ou venue d'un lien) les remplace.
const engineSettings = (id: string) => (RUNTIMES.some((x) => x.id === id) ? { util: "", oh: "" } : { util: DEFAULTS.util, oh: DEFAULTS.oh });
/** Fiche d'une entrée du catalogue : la famille Ollama d'abord dans la liste Ollama (« deepseek-r1 » y est le 8B distillé),
 *  puis sa clé, puis le dépôt (variante datée, checkpoint natif : presetForRepo), puis la famille Ollama. Une seule règle pour
 *  le HTML prérendu et pour les listes : avec deux règles, la page montrait un modèle puis passait à un autre. */
function entryPreset(key: string, m: CatalogModel, ollama = false) {
  const byOllama = m.ollama ? MODELS.find((p) => p.id === OLLAMA_DEFAULT[m.ollama!.name]) : undefined;
  const preset = (ollama ? byOllama : undefined) ?? MODELS.find((p) => p.id === key) ?? (m.repo ? presetForRepo(m.repo) : undefined) ?? byOllama;
  return { preset, byOllama };
}
// Modèle affiché sur une page sans lien : le n°1 des tendances Hugging Face. Le HTML prérendu montre le premier des
// tendances qui a une fiche (instantané du build, sans requête), avec la même règle que la liste (entryPreset) : à
// l'hydratation, rien ne change. Le navigateur ne passe ensuite à un autre modèle que si le n°1 de l'heure a changé
// depuis le build (et le lit sur Hugging Face s'il n'a pas de fiche). 23 octobre 2026 : le prérendu sautait les
// variantes (DeepSeek-V4.1-Flash-1017, rangée sous son dépôt), montrait le n°2 (Recursive Flint) puis basculait.
const FIRST_ROW = CATALOG_SNAPSHOT.rankings.huggingface
  .map((key) => ({ key, m: CATALOG_SNAPSHOT.models[key], preset: entryPreset(key, CATALOG_SNAPSHOT.models[key]).preset }))
  .find(({ preset }) => preset);
const FIRST_TREND = FIRST_ROW?.preset ?? MODELS.find((m) => m.id === DEFAULTS.model)!;


export default function LLMVramCalculatorPage() {
  const [modelId, setModelId] = useState(FIRST_TREND.id);
  const [custom, setCustom] = useState(CUSTOM_START);
  const [mode, setMode] = useState<Mode>("inference");
  const [weightFormat, setWeightFormat] = useState(FIRST_TREND.native ? "native" : "bf16");
  const [kvFormat, setKvFormat] = useState(FIRST_TREND.fp8Kv ? "fp8" : DEFAULTS.kv);
  const [context, setContext] = useState(DEFAULTS.ctx);
  const [sequences, setSequences] = useState(DEFAULTS.req);
  const [gpuId, setGpuId] = useState(DEFAULTS.gpu);
  const [vram, setVram] = useState(DEFAULTS.mem);
  const [tp, setTp] = useState(DEFAULTS.tp);
  const [pp, setPp] = useState(DEFAULTS.pp);
  const [gpuCount, setGpuCount] = useState(DEFAULTS.n);
  const [runtimeId, setRuntimeId] = useState(DEFAULTS.engine);
  const [utilization, setUtilization] = useState(DEFAULTS.util);
  const [overhead, setOverhead] = useState(DEFAULTS.oh);
  const [display, setDisplay] = useState(DEFAULTS.disp === "1");
  const [gradCkpt, setGradCkpt] = useState(true);
  const [shardStates, setShardStates] = useState(true);
  const [loraPct, setLoraPct] = useState(DEFAULTS.lora);
  const [ramGb, setRamGb] = useState(DEFAULTS.ram); // RAM du poste, pour le déchargement (llama.cpp)
  const [ramTypeId, setRamTypeId] = useState(DEFAULTS.ramt);
  const [hfInput, setHfInput] = useState("");
  const [hfModel, setHfModel] = useState<ModelPreset | null>(null);
  const [hfState, setHfState] = useState<{ loading?: boolean; error?: string }>({});
  const [autoRepo, setAutoRepo] = useState<string | null>(null); // n°1 des tendances en cours de chargement
  const [copied, setCopied] = useState<boolean | null>(null);
  const [deployEngine, setDeployEngine] = useState<DeployEngine>("vllm");
  const [cmdCopied, setCmdCopied] = useState(false);
  const [mcpClient, setMcpClient] = useState(MCP_CLIENTS[0].id);
  const [mcpCopied, setMcpCopied] = useState(false);
  // GPU du visiteur, lu au clic dans son navigateur (detect.ts) ; « hidden » : nom masqué par le navigateur.
  const [detected, setDetected] = useState<Detected | "hidden" | null>(null);
  // Barre de réponse collée en bas : seulement quand la ligne de réponse est hors de l'écran (sous la barre de
  // navigation fixe, ou cachée par la barre elle-même), sur mobile comme sur ordinateur.
  const [answerInView, setAnswerInView] = useState(true);
  const watchAnswer = useCallback((el: HTMLElement | null) => {
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setAnswerInView(e.isIntersecting), { rootMargin: "-72px 0px -64px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const [announce, setAnnounce] = useState(""); // zone lue par les lecteurs d'écran (role=status)
  const interacted = useRef(false);
  const engineChosen = useRef(false); // moteur choisi par le visiteur : un changement de GPU le garde
  const linked = useRef(false); // ouvert depuis un lien partagé : le n°1 des tendances ne le remplace pas
  const { prices, stale: pricesStale } = usePrices();

  const training = mode !== "inference";
  const runtime = RUNTIMES.find((r) => r.id === runtimeId) ?? RUNTIMES[0];

  const customUsed = useMemo(() => ({
    params: bound(custom.params, 0.001, 100_000), layers: bound(custom.layers, 1, 1000), kvHeads: bound(custom.kvHeads, 1, 1024),
    headDim: bound(custom.headDim, 1, 4096), hidden: bound(custom.hidden, 1, 1_000_000), vocab: bound(custom.vocab, 1, 10_000_000),
  }), [custom]);
  const model: ModelPreset = useMemo(
    () => modelId === CUSTOM_ID ? customModel(customUsed)
      : modelId === HF_ID && hfModel ? hfModel
      : MODELS.find((m) => m.id === modelId) ?? MODELS[0],
    [modelId, customUsed, hfModel],
  );

  // « Natif » sans checkpoint quantifié (lien partagé, modèle importé) = BF16.
  const wf = weightFormat === "native" && !model.native ? "bf16" : weightFormat;
  // Tokens et requêtes : des entiers (pas de « 2.5 concurrent requests »).
  const ctx = Math.round(bound(context, 1, 1 << 24));
  const nSeq = Math.round(bound(sequences, 1, 100_000));
  const nSeqText = nSeq.toLocaleString("en-US");
  const nVram = bound(vram, 1, 100_000);
  // Calcul sur la mémoire que le pilote expose (L40S « 48 GB » : 44,99 GiB) tant que le champ garde
  // la valeur annoncée du GPU choisi ; une valeur saisie à la main est prise telle quelle.
  const gpuPicked = GPUS.find((g) => g.id === gpuId);
  const memGiB = gpuPicked && nVram === gpuPicked.vram ? usableMemGiB(gpuPicked) : nVram;
  const utilUser = utilization.trim() !== "", ohUser = overhead.trim() !== "";
  const nUtil = bound(utilization, 1, 100);
  const nOverhead = bound(overhead, 0, 1000);
  const nLora = bound(loraPct, 0, 100);
  const kvBytes = (KV_FORMATS.find((f) => f.id === kvFormat) ?? KV_FORMATS[0]).bytes;
  // Catalogue de la veille (chaque heure) : tendances, popularité, prise en charge par les
  // moteurs, fichiers GGUF publiés. Instantané du build pour le rendu serveur, puis le fichier de l'heure.
  const catalog = useLiveData("/data/catalog.json", CATALOG_SNAPSHOT, validateCatalog) ?? CATALOG_SNAPSHOT;
  const entry = catalog.models[model.id] ?? (model.repo ? catalog.models[model.repo] : undefined);
  const hfPick = useMemo(() => (hfModel ? { ...hfModel, id: HF_ID } : undefined), [hfModel]);
  // Taille exacte des fichiers GGUF publiés, quand le format en est un (sinon l'estimation).
  const exactFor = (fid: string) => ggufSize(entry, fid)?.bytes;
  const exactNow = training ? undefined : ggufSize(entry, wf);
  // llama.cpp (Ollama, LM Studio) : couches réparties entre GPU, embedding d'entrée en RAM.
  const layerSplit = runtime.id === "llamacpp" && !training;
  const gpu = GPUS.find((g) => g.id === gpuId);
  // Moteur et GPU visés : compute() applique ce que ce moteur réserve sur ce GPU (reserve.ts), sauf valeur
  // saisie. GPU personnalisé ou entraînement : les valeurs génériques du moteur.
  const engineTarget = !training && gpu ? { engine: runtime.id, gpu, display, userUtilization: utilUser, userOverhead: ohUser } : undefined;
  const base = {
    model, mode, kvBytes, context: ctx, sequences: nSeq,
    weightBits: (WEIGHT_FORMATS.find((f) => f.id === wf) ?? WEIGHT_FORMATS[1]).bits,
    exactWeightBytes: training ? undefined : exactFor(wf), layerSplit,
    tp: 1, pp: 1,
    vramGiB: memGiB, utilization: utilUser ? nUtil / 100 : runtime.utilization, overheadGiB: ohUser ? nOverhead : runtime.overheadGiB, target: engineTarget,
    gradientCheckpointing: gradCkpt, shardStates, loraFraction: nLora / 100,
  };
  // Le nombre de GPU est la réponse, pas un réglage : Auto prend le plus petit qui
  // tient. TP × PP à la main reste possible pour qui sait ce qu'il fait.
  const autoGpus = minGpus(base);
  // Console : une seule, llama.cpp ne répartit pas un modèle entre plusieurs PS5.
  const consoleGpu = !!gpu && isConsole(gpu);
  // Une seule machine : la console, ou un Mac avec Transformers (qui ne répartit pas un modèle entre Mac).
  const oneBox = consoleGpu || (!training && singleDevice(gpu, runtimeId));
  const manual = gpuCount === "manual" && !training && !oneBox;
  const fixedGpus = oneBox ? 1 : gpuCount === "auto" || gpuCount === "manual" ? null : num(gpuCount);
  // Entraînement : chaque GPU garde les activations de son propre lot (parallélisme de données), et sans états
  // partagés une copie entière des poids et de l'optimiseur. Si cela dépasse déjà un GPU, aucun nombre n'y suffit :
  // on montre un seul GPU plutôt que « plus de 128 » (128 Mac M1 annoncés pour un QLoRA de gpt-oss 20B).
  const one = training && autoGpus === null ? compute({ ...base, ...splitFor(1, layerSplit) }) : null;
  const perGpuFloor = one ? one.activationsGiB + one.overheadGiB + (shardStates ? 0 : one.weightsGiB + one.gradsOptimGiB) : 0;
  const neverFits = !!one && perGpuFloor > one.budgetGiB;
  // Manuel : des entiers (pas de « pipeline parallel 1.5 »), et pas plus d'étages de pipeline que de couches.
  const manualTp = Math.round(bound(tp, 1, 1024)), manualPp = Math.min(Math.round(bound(pp, 1, 1024)), Math.max(1, model.layers));
  const inputs = { ...base, ...(manual ? { tp: manualTp, pp: manualPp } : splitFor(fixedGpus ?? autoGpus ?? (neverFits ? 1 : 128), layerSplit)) };
  const r = compute(inputs);
  // Écran affiché : compté à part (reserve.ts), hors du champ « Overhead ».
  const displayGiB = engineTarget?.display && gpu && !isDatacenter(gpu) && !isUnified(gpu) ? DISPLAY_GIB : 0;
  const shownUtil = utilUser ? nUtil : Math.round(r.utilization * 1000) / 10;
  const shownOverhead = ohUser ? nOverhead : Math.round((r.overheadGiB - displayGiB) * 10) / 10;
  // Commande de déploiement : un moteur qui tourne sur ce GPU (sur Mac, llama.cpp).
  const deployTab = runsEngine(gpu, deployEngine) ? deployEngine : DEPLOY_ENGINES.find((e) => runsEngine(gpu, e.id))?.id ?? deployEngine;
  const gpuName = gpu?.label ?? `${nVram.toLocaleString("en-US")} GB GPU`;
  const formatLabel = (id: string) =>
    id === "native" ? model.native?.label ?? "Native" : WEIGHT_FORMATS.find((f) => f.id === id)?.label.split(" · ")[0] ?? id;
  const weightLabel = formatLabel(wf);
  const headline = !manual && !fixedGpus && autoGpus === null && !neverFits ? `More than 128 × ${gpuName}` : `${r.gpus.toLocaleString("en-US")} × ${gpuName}`;
  // Pourquoi plus de GPU n'aident pas (entraînement) : le lot de chaque GPU, ou la copie complète sans états partagés.
  const neverFitsWhy = !one ? "" : shardStates || one.activationsGiB + one.overheadGiB > one.budgetGiB
    ? `each GPU holds the activations of its own batch (${gib(one.activationsGiB)})`
    : "without sharded optimizer states, each GPU holds a full copy of the weights and optimizer";
  const offers = gpu ? offersFor(prices, gpu.id) : [];
  const costUsd = offers.length && r.status !== "oom" ? r.gpus * offers[0].price : null;
  const cost = costUsd === null ? null : perHour(costUsd, r.gpus);
  const providerNames = Object.values(prices.providers).map((p) => p.name.split(" (")[0]).join(", ");
  const gpuShort = gpu ? gpuName0(gpu) : gpuName.split(" · ")[0];
  // Vitesse (estimation par la bande passante) et coût face à l'API du même modèle.
  const speed = gpu && r.status !== "oom" ? decodeSpeed(inputs, gpu, runtime.bwEfficiency) : null;
  // Temps avant le premier token : traitement d'un prompt de la taille du contexte choisi.
  const ttft = speed && gpu
    ? prefillSeconds(model, ctx, gpu, inputs.tp * inputs.pp, layerSplit, inputs.pp)
    : null;
  const apiKey = model.preview?.api ?? model.repo;
  // Un seul prix d'API : le moins cher entre OpenRouter et les fournisseurs de Hugging Face (cheapestApi).
  const api = cheapestApi(openRouterPrice(prices.apis, apiKey), entry?.apis?.providers);
  const economics = speed && offers.length ? selfHostVsApi(r.gpus * offers[0].price, speed.total, api?.output ?? Infinity) : null;
  // Pas assez de VRAM sur un poste (pas un serveur, pas un Mac) : llama.cpp garde une partie des
  // poids en RAM. Proposé quand le modèle ne tient pas sur le nombre de GPU choisi, ou qu'Auto en
  // demande plusieurs. Calculé avec les réglages de llama.cpp, quel que soit le moteur choisi.
  const llamaRt = RUNTIMES.find((x) => x.id === "llamacpp")!;
  const ramType = RAM_TYPES.find((t) => t.id === ramTypeId) ?? RAM_TYPES[1];
  // 8 To au plus : au-delà de ce qu'un serveur accueille (EPYC : 6 To), un chiffre tapé par erreur.
  const nRam = bound(ramGb, 0, 8192);
  const ramText = nRam.toLocaleString("en-US");
  const offloadGpus = fixedGpus ?? (manual ? r.gpus : 1);
  const offloadAsked = !training && !!gpu && !isDatacenter(gpu) && !isUnified(gpu) && (r.status === "oom" || (gpuCount === "auto" && autoGpus !== 1));
  const offload = offloadAsked && gpu ? offloadPlan(
    { ...base, ...splitFor(offloadGpus, true), layerSplit: true, utilization: llamaRt.utilization, overheadGiB: llamaRt.overheadGiB, target: { engine: "llamacpp", gpu, display } },
    gpu, offloadGpus, { gib: nRam, bandwidth: ramType.bandwidth }, llamaRt.bwEfficiency,
  ) : null;
  // Trop grand pour la VRAM, mais llama.cpp le fait tourner avec la RAM : c'est la réponse à donner
  // d'abord. Un visiteur arrivé par un lien (5090 et Qwen3.8-Flash-Next) lisait « Does not fit ».
  const withRam = r.status === "oom" && !!offload?.plan;
  const statusClass = withRam ? "tight" : r.status;
  const statusLabel = STATUS_LABEL[withRam ? "ram" : r.status];
  // Lecteurs d'écran : la réponse est relue quand elle change après une action de l'utilisateur
  // (pas au chargement, ni quand les prix du jour arrivent), une fois la saisie terminée.
  const answerSummary = `${statusLabel}: ${headline}${costUsd === null ? "" : `, from $${usd2(costUsd)} per hour`}.`;
  const lastSummary = useRef(answerSummary);
  useEffect(() => {
    if (answerSummary === lastSummary.current) return;
    if (!interacted.current) { lastSummary.current = answerSummary; return; }
    const t = setTimeout(() => { lastSummary.current = answerSummary; setAnnounce(answerSummary); }, 800);
    return () => clearTimeout(t);
  }, [answerSummary]);
  const offloadCommand = offload?.plan ? deployCommand("llamacpp", {
    repo: servedRepo(model, wf), weightFormat: wf, nativeLabel: model.native?.label, kvFormat, context: ctx, sequences: nSeq,
    tp: 1, pp: offloadGpus, utilization: 0.95, ggufRepo: entry?.ggufFiles?.repo, ggufQuants: Object.keys(entry?.ggufFiles?.files ?? {}),
    offloadFlag: offload.plan.flag, yarn: model.yarn,
  }) : "";
  // Commande de lancement, précédée de ce que les registres officiels disent de ce modèle.
  const engines = entry?.engines;
  // Version d'Ollama (registre, chaque jour) : son format et sa taille exacte ; dans le format choisi ici,
  // l'écart avec l'estimation quand il dépasse 5 % (gpt-oss : Ollama garde l'attention en BF16, 1,6 GiB
  // de plus que le GGUF de la commande).
  const ollamaInfo = (() => {
    const o = entry?.ollama;
    if (!o?.bytes) return "";
    const fmt = o.quant === "MXFP4" && /^MXFP4/.test(model.native?.label ?? "") ? "native" : OLLAMA_FORMATS[o.quant ?? ""];
    const bits = WEIGHT_FORMATS.find((f) => f.id === wf)?.bits;
    const est = fmt === wf && bits !== undefined ? exactFor(wf) ?? weightBytes(model, engineBits({ model, weightBits: bits, layerSplit: true })) : 0;
    const gap = est ? (o.bytes - est) / GiB : 0;
    return ` (${o.quant ? `${o.quant}, ` : ""}${gib(o.bytes / GiB)} of weights${Math.abs(gap) * GiB > 0.05 * est ? `, ${gib(Math.abs(gap))} ${gap > 0 ? "more" : "less"} than this estimate` : ""})`;
  })();
  const deployNotes = [
    model.preview && "# Weights not published yet: replace <your-org>/<your-model> with the official Hugging Face repo",
    // Hors du registre de vLLM : l'installation que demande la fiche du modèle (plugin du labo), sinon un avertissement.
    deployTab === "vllm" && engines?.vllm === false && (engines.vllmCard
      ? engines.vllmSetup
        ? `# Not in upstream vLLM: the model card installs this first\n${engines.vllmSetup}`
        : `# Not in upstream vLLM: follow the setup on the model card first (https://huggingface.co/${model.repo})`
      : `# vLLM does not list ${engines.arch ?? "this architecture"} yet: check the model card for a plugin, or pick another engine`),
    deployTab === "vllm" && engines?.recipe && `# Official vLLM recipe for this model: ${engines.recipe}`,
    // Hors du registre de SGLang (Unlimited-OCR 2 : un SGLang modifié, fourni dans le dépôt du modèle).
    deployTab === "sglang" && engines?.sglang === false && `# SGLang does not list ${engines.arch ?? "this architecture"} for this model: check the model card for a build that does (https://huggingface.co/${model.repo})`,
    // Hors du convertisseur de llama.cpp : une version GGUF publiée, lisible ou pas par llama.cpp.
    deployTab === "llamacpp" && engines?.llamacpp === false && (engines.gguf
      ? engines.gguf.llamacpp
        ? `# GGUF build that llama.cpp runs: https://huggingface.co/${engines.gguf.repo}`
        : `# GGUF builds (https://huggingface.co/${engines.gguf.repo}) need a patched llama.cpp: ${engines.gguf.arch} is not in mainline yet`
      : `# llama.cpp cannot convert ${engines.arch ?? "this architecture"} to GGUF yet: no GGUF build will exist until it does`),
    deployTab === "llamacpp" && entry?.ollama && `# With Ollama: ${ollamaCommand(entry.ollama)}${ollamaInfo}`,
    // Ollama choisit son contexte par défaut selon la VRAM (docs.ollama.com/context-length) : le rappeler, avec
    // la variable qui donne celui de cette page.
    deployTab === "llamacpp" && entry?.ollama && `# Ollama's default context follows your VRAM (4K under 24 GiB, 32K up to 48 GiB, 256K above). For ${tokens(ctx)}: OLLAMA_CONTEXT_LENGTH=${ctx} ollama serve`,
    // SGLang, modèle hybride : sans --max-running-requests, sa réserve d'états prend 0,9 × la mémoire du cache
    // KV (mamba_full_memory_ratio), le contexte disponible est à peu près divisé par deux.
    deployTab === "sglang" && model.stateBytesPerSeq && "# Keep --max-running-requests: it also sizes SGLang's recurrent-state pool. Without it, the states get 0.9× the KV cache memory, about half the context",
  ].filter(Boolean);
  // PlayStation 5 : pas de commande, les étapes de PS5LM Plus (qui lance llama.cpp lui-même).
  const command = consoleGpu ? ps5Steps({ repo: servedRepo(model, wf), weightFormat: wf, nativeLabel: model.native?.label, ggufRepo: entry?.ggufFiles?.repo, ggufQuants: Object.keys(entry?.ggufFiles?.files ?? {}) })
    : (deployNotes.length ? `${deployNotes.join("\n")}\n` : "") +
    deployCommand(deployTab, {
      repo: servedRepo(model, wf), weightFormat: wf, nativeLabel: model.native?.label, kvFormat,
      context: ctx, sequences: nSeq, tp: inputs.tp, pp: inputs.pp,
      utilization: utilUser ? nUtil / 100 : gpu ? engineReserve(deployTab, gpu, inputs.tp, inputs.pp, model).utilization : runtime.utilization,
      ggufRepo: entry?.ggufFiles?.repo, ggufQuants: Object.keys(entry?.ggufFiles?.files ?? {}),
      offloadFlag: r.status === "oom" && offload?.plan ? offload.plan.flag : undefined, yarn: model.yarn,
      mistralFormat: !!model.mistralFormat && servedRepo(model, wf) === model.repo,
    });
  const hasReferral = offers.some((o) => REFERRAL_LINKS[o.id]);
  const priceGpus = r.status === "oom" ? 1 : r.gpus; // coût du module de prix : la config choisie, si elle tient
  const modelLab = labOf(model, catalog);
  const modelLicense = entry && licenseInfo(entry.license, entry.licenseName);
  // Fine-tune d'un modèle de la liste : même architecture, la fiche d'origine fait référence.
  const parentPreset = entry?.parent ? MODELS.find((m) => m.repo.toLowerCase() === entry.parent!.toLowerCase()) : undefined;
  const pricesDate = prices.updated.slice(0, 10); // AAAA-MM-JJ : identique au serveur et au client (pas de fuseau)
  const pricesStamp = priceStamp(prices.updated);
  const splitText = [inputs.tp > 1 && `tensor parallel ${inputs.tp}`, inputs.pp > 1 && `pipeline parallel ${inputs.pp}`].filter(Boolean).join(" × ");

  // Ce que le calculateur propose (llmVram/choices.ts) : GPU les moins chers dans un seul serveur,
  // formats, grille « Ça tient ? », autres modèles ; seulement ce qui tourne avec ce moteur sur ce GPU.
  const exactOf: ExactSize = (m, id) => (m.id === model.id ? exactFor(id) : ggufSize(catalog.models[m.id], id)?.bytes);
  const options = cheapestSetups(base, runtimeId, wf, prices, training);
  // La moins chère, affichée dans la réponse quand elle coûte nettement moins que le GPU choisi
  // (ou quand il ne suffit pas) : sinon elle n'apparaît qu'à l'étape 3.
  const cheaper = options[0] && Number.isFinite(options[0].cost) && options[0].g.id !== gpuId
    && (costUsd === null || options[0].cost < costUsd * 0.9) ? options[0] : undefined;

  const quantRows = training ? [] : formatChoices(base, gpu, runtimeId, exactOf);
  const quality = (id: string, bits: number) => (id === "native" ? "Official weights" : qualityHint(bits));
  // Nombre de GPU imposé qui ne suffit pas : le format le plus fidèle qui y tient.
  const target = manual ? r.gpus : fixedGpus;
  const betterQuant = target && r.status === "oom" ? quantRows.find((q) => q.n !== null && q.n <= target) : undefined;

  // « Ça tient ? » d'un coup d'œil : chaque format de poids à chaque longueur de contexte, sur le
  // matériel choisi (même nombre de GPU, mêmes requêtes simultanées). Un clic applique la case.
  const matrixSplit = manual ? { tp: inputs.tp, pp: inputs.pp } : splitFor(r.gpus, layerSplit);
  // Carte grand public avec llama.cpp : les cases qui tiennent avec la RAM du visiteur sont marquées.
  const gridRam = !training && runtimeId === "llamacpp" && !!gpu && !isDatacenter(gpu) && !isUnified(gpu)
    ? { gpu, ram: { gib: nRam, bandwidth: ramType.bandwidth }, utilization: llamaRt.utilization, overheadGiB: llamaRt.overheadGiB, bwEfficiency: llamaRt.bwEfficiency }
    : undefined;
  const { contexts: matrixCtx, rows: matrix } = fitGrid(base, matrixSplit, gpu, runtimeId, quantRows, exactOf, gridRam);
  // Sélecteur de modèles : pastille « tient » sur le nombre de GPU imposé (un seul en Auto), fiche du
  // catalogue (téléchargements, score ECI), modèle chargé depuis Hugging Face en tête.
  const pickerGpus = fixedGpus ?? (manual ? r.gpus : 1);
  // Strata (scripts/sources/strata.ts, via le catalogue) : sur une carte grand public seulement.
  const strata = !training && gpu && !isDatacenter(gpu) && !isUnified(gpu) ? entry?.strata : undefined;
  // Mesures du projet sur son PC le plus rapide : la version la plus rapide et la plus lente.
  const strataFast = strata && strataFastest(strata);
  const strataSlow = strata && strataFast && [...strata.speeds].filter((x) => x.setup === strataFast.setup).sort((a, b) => a.write - b.write)[0];
  const catalogEntry = (m: ModelPreset) => catalog.models[m.id] ?? (m.repo ? catalog.models[m.repo] : undefined);
  // Pour chaque format, le plus long contexte qui tient : une ligne par format sur mobile (la grille
  // y serait plus large que l'écran), un résumé à côté de la grille ailleurs. Avec la RAM en plus
  // quand elle va plus loin que la VRAM seule.
  const fitMax = matrix.map(({ f, cells }) => {
    const last = (keep: (st: string) => boolean) => { let k = cells.length - 1; while (k >= 0 && !keep(cells[k])) k--; return k; };
    const j = last((st) => st !== "oom"), v = last((st) => st === "ok" || st === "tight");
    const inVram = v < 0 ? "" : `up to ${tokens(matrixCtx[v])}${cells[v] === "tight" ? " (tight)" : ""}`;
    const text = j < 0 ? `does not fit at ${tokens(matrixCtx[0])}`
      : j === v ? inVram
      : `${inVram ? `${inVram}, ${tokens(matrixCtx[j])}` : `up to ${tokens(matrixCtx[j])}`} with RAM`;
    return { f, j, text };
  });
  const fitGroups: { from: string; to: string; text: string }[] = [];
  for (const { f, text } of fitMax) {
    const last = fitGroups.at(-1);
    if (last?.text === text) last.to = formatLabel(f.id);
    else fitGroups.push({ from: formatLabel(f.id), to: formatLabel(f.id), text });
  }
  // Les autres modèles de la liste qui tiennent sur ce matériel, au format le plus fidèle qui passe,
  // du plus grand au plus petit.
  const otherModels = training || !gpu ? []
    : otherModelsThatFit(base, matrixSplit, gpu, runtimeId, exactOf).map(({ m, f }) => ({ m, f, lab: labOf(m, catalog) }));

  // InferenceX : dernières mesures réelles de ce modèle (catalogue). Le calculateur garde son estimation ;
  // pour une configuration identique (GPU, nombre de cartes, moteur, format), il la montre à côté de la
  // mesure, dans ses conditions (part de la mémoire réservée, requêtes à la fois, prompt et réponse de 1K).
  const ixRuns = training ? [] : entry?.verified?.inferencex?.runs ?? [];
  const ixMatch = ixRuns.find((x) => x.gpu === gpuId && x.engine === runtimeId && x.format === wf && x.gpus === inputs.tp && inputs.pp === 1);
  const ixRows = !ixMatch || !gpu ? [] : (() => {
    const x = ixMatch, rows: { key: string; label: string; real: string; ours: string; gap: string; cmp?: ReturnType<typeof compareGiB> }[] = [];
    const bits = (WEIGHT_FORMATS.find((f) => f.id === x.format) ?? WEIGHT_FORMATS[1]).bits;
    const at = { ...inputs, weightBits: bits, exactWeightBytes: undefined, layerSplit: false, tp: x.gpus, pp: 1 };
    const gap = (real: number, ours: number) => `${ours >= real ? "+" : ""}${(((ours - real) / real) * 100).toFixed(1)}%`;
    const w = weightsPerGpu(at, x.gpus, 1) / GiB;
    if (x.weightsGiB) rows.push({ key: "weights", label: "Weights, per GPU", real: gib(x.weightsGiB), ours: gib(w), gap: gap(x.weightsGiB, w), cmp: compareGiB(x.weightsGiB, w) });
    if (x.kvGiB) {
      const own = engineReserve(x.engine, gpu, x.gpus, 1, model);
      const util = x.utilization ?? own.utilization, pool = Math.max(0, usableMemGiB(gpu) * util - w - own.overheadGiB);
      rows.push({
        key: "kv", label: `KV cache pool, per GPU (${Math.round(util * 100)}% of memory reserved)`,
        real: `${gib(x.kvGiB)}${x.kvTokens ? `, ${bigCount(x.kvTokens)} tokens` : ""}`, ours: gib(pool), gap: gap(x.kvGiB, pool), cmp: compareGiB(x.kvGiB, pool),
      });
    }
    const speed = x.tps && x.conc ? decodeSpeed({ ...at, sequences: x.conc, context: 1536 }, gpu, runtime.bwEfficiency) : null;
    // Vitesse : estimation annoncée à ±30 %, écart affiché sans verdict de couleur.
    if (speed && x.tps) rows.push({ key: "speed", label: `Speed per request, ${x.conc} at once`, real: `${x.tps.toFixed(0)} tokens/s`, ours: `${speed.perRequest.toFixed(0)} tokens/s`, gap: gap(x.tps, speed.perRequest) });
    return rows;
  })();
  const ixEngine = (e: string) => (e === "sglang" ? "SGLang" : "vLLM");
  const ixGpu = (id: string) => { const g = GPUS.find((x) => x.id === id); return g ? gpuName0(g).replace(/^(NVIDIA|AMD) /, "") : id; };
  // Configurations mesurées à essayer : une par GPU et par moteur, les plus louées d'abord.
  const ixChoices = ixRuns.filter((x, i, all) => all.findIndex((y) => y.gpu === x.gpu && y.engine === x.engine) === i).slice(0, 6);
  const applyRun = (x: (typeof ixRuns)[number]) => {
    const g = GPUS.find((y) => y.id === x.gpu);
    if (!g) return;
    interacted.current = true;
    engineChosen.current = true;
    setGpuId(g.id);
    setVram(String(g.vram));
    apply(coherentSetup(model, { gpu: g, engine: x.engine, weights: x.format, kv: kvFormat }));
    setGpuCount(String(x.gpus));
    if (x.conc) setSequences(String(x.conc));
  };
  // Même liste, regroupée par nombre de GPU (le moins d'abord, « plus de 128 » en dernier) ;
  // dans un groupe, l'ordre d'origine : du format le plus fidèle au plus compressé.
  const quantGroups = [...new Set(quantRows.map((q) => q.n))]
    .sort((a, b) => (a ?? Infinity) - (b ?? Infinity))
    .map((n) => ({ n, rows: quantRows.filter((q) => q.n === n) }));
  const quantOrder = quantGroups.flatMap(({ rows }) => rows.map(({ f }) => f.id)); // ordre affiché

  const kvPerSeq = kvBytesPerSequence(model, ctx, kvBytes);
  const hwWarnings = gpu && !training
    ? formatWarnings(gpu, runtimeId === "llamacpp" || runsNatively(gpu, model, wf) ? undefined : formatNeeds(model, wf), kvFormat === "fp8", runtimeId === "llamacpp" ? undefined : wf)
    : [];
  const allFullKv = model.layers * Math.max(...model.kv.map((g) => g.width)) * ctx * kvBytes;

  // Barre empilée à l'échelle du budget, ou du total quand il le dépasse.
  const scale = Math.max(r.budgetGiB, r.totalGiB) || 1;
  const segments = [
    { key: "weights", label: mode === "lora" || mode === "qlora" ? "Frozen weights" : "Weights", value: r.weightsGiB },
    { key: "kv", label: "KV cache", value: r.kvGiB },
    { key: "state", label: "Recurrent state", value: r.stateGiB },
    { key: "optim", label: mode === "full" ? "Gradients + optimizer" : "Adapters + optimizer", value: r.gradsOptimGiB },
    { key: "acts", label: "Activations", value: r.activationsGiB },
    { key: "overhead", label: "Runtime overhead", value: r.overheadGiB },
  ].filter((s) => s.value > 0);

  const hfPending = useRef<string | null>(null); // dépôt Hugging Face en cours de chargement
  // GPU, moteur, poids et cache tels qu'affichés. Chaque changement en calcule une nouvelle combinaison
  // qui tourne (llmVram/choices.ts) ; apply la pose, et les réglages du moteur suivent le moteur.
  const setup: Setup = { gpu, engine: runtimeId, weights: wf, kv: kvFormat };
  const applyEngine = (id: string) => {
    setRuntimeId(id);
    const engine = DEPLOY_ENGINES.find((e) => e.id === id);
    if (engine) setDeployEngine(engine.id);
    if (RUNTIMES.some((x) => x.id === id)) { setUtilization(""); setOverhead(""); }
  };
  const apply = (next: Setup) => {
    if (next.engine !== runtimeId) applyEngine(next.engine);
    if (next.weights !== wf) setWeightFormat(next.weights);
    if (next.kv !== kvFormat) setKvFormat(next.kv);
  };
  const onModel = (id: string, picked = id === HF_ID ? hfModel : MODELS.find((m) => m.id === id)) => {
    hfPending.current = null; // un chargement Hugging Face en cours ne remplacera plus ce choix
    setModelId(id);
    // Checkpoint officiel s'il tourne ici (FP8 sur un A100 : INT8 ; avec llama.cpp : Q8_0), cache FP8
    // calibré seulement si le GPU et le moteur le permettent.
    if (picked) apply(setupAfterModel(picked, setup));
    else if (weightFormat === "native") setWeightFormat("bf16");
  };
  const quickPick = (id: string) => {
    interacted.current = true;
    onModel(id);
    jumpTo("vram-calculator");
  };
  // Un modèle hors de la liste se charge comme un lien Hugging Face collé.
  const sizeRepo = (repo: string) => {
    interacted.current = true;
    void loadRepo(repo);
    jumpTo("vram-calculator");
  };
  // Classements du catalogue, chaque modèle avec son preset s'il en a un (clé = id du calculateur).
  // name : celui de la fiche pour un modèle de la liste, le nom du dépôt mis au propre sinon ;
  // un modèle seulement sur Ollama garde le nom d'Ollama.
  // Chaque ligne mène à une fiche : le preset du modèle, celui dont c'est le checkpoint natif ou une variante (presetForRepo),
  // ou celui de la famille Ollama (OLLAMA_DEFAULT : le modèle que « ollama run » télécharge sans précision de taille).
  // Liste Ollama : le modèle par défaut de la famille d'abord (« deepseek-r1 » y est le 8B distillé, pas le 671B).
  const ranked = (keys: string[], ollama = false) => keys.map((key) => {
    const m = catalog.models[key];
    const { preset, byOllama } = entryPreset(key, m, ollama);
    // Nom affiché : celui de la fiche quand la ligne y mène tout droit (sa clé, la famille Ollama), sinon celui du dépôt.
    const named = ollama && byOllama ? byOllama : preset && (preset.id === key || !m.repo) ? preset : undefined;
    return { key, m, preset, name: named ? modelName(named) : m.repo ? tidyLabel(prettyModelName(m.name), m.repo) : m.name };
  });
  // Tendances Hugging Face, sans les modèles que le calculateur ne sait pas lire (sauf s'ils ont une fiche).
  const trendRows = ranked(catalog.rankings.huggingface).filter(({ m, preset }) => preset || m.sizable !== false);
  const ollamaRows = ranked(catalog.rankings.ollama, true);
  // Raccourcis du haut : le top 5 des tendances ; sans catalogue, les dernières sorties.
  const picks = trendRows.length
    ? trendRows.slice(0, 5).map(({ m, preset, name }) => ({ repo: m.repo ?? "", preset, label: name, lab: labOfOrg(m.lab ?? "", catalog) }))
    : LATEST.slice(0, 5).map((m) => ({ repo: m.repo, preset: m as ModelPreset | undefined, label: modelName(m), lab: labOf(m, catalog) }));
  // Onglets des nouveautés : flèches gauche/droite pour passer de l'un à l'autre (motif ARIA « tabs »).
  type NewsTab = "trending" | "ollama" | "latest";
  const newsTabs = (["trending", "ollama", "latest"] as NewsTab[])
    .filter((t) => (t === "trending" ? trendRows.length : t === "ollama" ? ollamaRows.length : true));
  const [newsTabPicked, setNewsTab] = useState<NewsTab>("trending");
  const newsTab = newsTabs.includes(newsTabPicked) ? newsTabPicked : newsTabs[0];
  const [newsMore, setNewsMore] = useState(false);
  // Listes de l'étape 3 dépliées sur mobile (« Show all »).
  const [listsOpen, setListsOpen] = useState({ quant: false, cheapest: false, models: false });
  const openList = (k: keyof typeof listsOpen) => setListsOpen((o) => ({ ...o, [k]: true }));
  const onNewsKey = (e: ReactKeyboardEvent) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault(); // Début/Fin feraient défiler la page
    const i = e.key === "Home" ? 0 : e.key === "End" ? newsTabs.length - 1
      : newsTabs.indexOf(newsTab) + (e.key === "ArrowRight" ? 1 : -1);
    const next = newsTabs[(i + newsTabs.length) % newsTabs.length];
    setNewsTab(next);
    setNewsMore(false);
    document.getElementById(`news-tab-${next}`)?.focus();
  };
  /** link : lien partagé, on garde le format de poids qu'il indique ; auto : n°1 des tendances,
   *  chargé sans indicateur ni message d'erreur, et abandonné si l'utilisateur a déjà agi. */
  const loadRepo = async (repo: string, how: "pick" | "link" | "auto" = "pick") => {
    // Modèle déjà en preset : on garde la version vérifiée à la main.
    const preset = MODELS.find((m) => m.repo.toLowerCase() === repo.toLowerCase());
    if (preset) {
      if (how === "link") setModelId(preset.id); else onModel(preset.id);
      return setHfState({});
    }
    if (how !== "auto") setHfState({ loading: true }); else setAutoRepo(repo);
    hfPending.current = repo;
    try {
      const m = await loadHfModel(repo);
      setHfModel(m);
      // L'utilisateur a choisi un autre modèle pendant le chargement : le modèle chargé
      // reste dans la liste, mais n'écrase pas son choix.
      if (hfPending.current !== repo || (how === "auto" && interacted.current)) return setHfState({});
      // Le modèle du HTML prérendu cède sa place : le cache KV repart du BF16 (onModel passe en FP8 si le modèle le prévoit).
      if (how === "auto") setKvFormat(DEFAULTS.kv);
      if (how === "link") setModelId(HF_ID); else onModel(HF_ID, m);
      setHfState({});
    } catch (err) {
      setHfState(hfPending.current === repo && how !== "auto" ? { error: err instanceof Error ? err.message : String(err) } : {});
    } finally {
      if (hfPending.current === repo) hfPending.current = null;
      if (how === "auto") setAutoRepo(null);
    }
  };
  const onHfSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (hfState.loading) return;
    const repo = parseHfRepo(hfInput);
    if (!repo) return setHfState({ error: "Paste a model link such as https://huggingface.co/Qwen/Qwen3.5-9B" });
    loadRepo(repo);
  };
  // Réglages ↔ URL : chaque configuration a un lien partageable. L'URL est lue après
  // l'hydratation (le HTML prérendu est celui des valeurs par défaut) puis réécrite à
  // chaque changement. Tout ce qui vient de l'URL est validé avant d'être appliqué.
  const urlFields: [keyof typeof DEFAULTS, string, (v: string) => void, (v: string) => boolean][] = [
    ["model", modelId, setModelId, (v) => v === CUSTOM_ID || MODELS.some((m) => m.id === v)],
    ["c", Object.values(custom).join(","), (v) => {
      const [params, layers, kvHeads, headDim, hidden, vocab] = v.split(",");
      setCustom({ params, layers, kvHeads, headDim, hidden, vocab });
    }, (v) => v.split(",").length === 6 && v.split(",").every(isNum)],
    ["mode", mode, (v) => setMode(v as Mode), (v) => Object.hasOwn(MODE_LABEL, v)],
    ["w", weightFormat, setWeightFormat, (v) => WEIGHT_FORMATS.some((f) => f.id === v)],
    ["kv", kvFormat, setKvFormat, (v) => KV_FORMATS.some((f) => f.id === v)],
    ["ctx", context, setContext, isPos],
    ["req", sequences, setSequences, isPos],
    ["gpu", gpuId, setGpuId, (v) => v === "custom" || GPUS.some((g) => g.id === v)],
    ["mem", vram, setVram, isPos],
    ["n", gpuCount, setGpuCount, (v) => v === "auto" || v === "manual" || GPU_COUNTS.includes(Number(v))],
    ["tp", tp, setTp, isNum],
    ["pp", pp, setPp, isNum],
    ["engine", runtimeId, setRuntimeId, (v) => RUNTIMES.some((rt) => rt.id === v)],
    ["util", utilization, setUtilization, isPct],
    ["oh", overhead, setOverhead, isNum],
    ["disp", display ? "1" : "0", (v) => setDisplay(v === "1"), (v) => v === "0" || v === "1"],
    ["lora", loraPct, setLoraPct, isNum],
    ["gc", gradCkpt ? "1" : "0", (v) => setGradCkpt(v === "1"), (v) => v === "0" || v === "1"],
    ["shard", shardStates ? "1" : "0", (v) => setShardStates(v === "1"), (v) => v === "0" || v === "1"],
    ["ram", ramGb, setRamGb, isNum],
    ["ramt", ramTypeId, setRamTypeId, (v) => RAM_TYPES.some((t) => t.id === v)],
  ];
  /** Lien de la configuration affichée. Le modèle y est toujours écrit (un lien sans « model »
   *  ouvre DEFAULTS.model) ; `pristine` : page intacte, adresse nue (le n°1 du moment). */
  const configUrl = (pristine: boolean) => {
    if (pristine) return location.pathname;
    const q = new URLSearchParams();
    const engineDefaults = engineSettings(runtimeId);
    for (const [key, value] of urlFields) {
      // La mémoire découle du GPU choisi : inutile de l'écrire si elle n'a pas été modifiée.
      if (key === "mem" && gpu && String(gpu.vram) === value) continue;
      // Mémoire utilisable et réserve : écrites seulement si elles diffèrent de celles du moteur.
      const fallback = key === "util" || key === "oh" ? engineDefaults[key] : key === "ctx" && mode !== "inference" ? TRAINING_CTX : DEFAULTS[key];
      if (key === "model" || value !== fallback) q.set(key, value);
    }
    const hf = modelId === HF_ID && hfModel ? hfModel.repo : hfPending.current;
    if (hf) { q.delete("model"); q.set("hf", hf); }
    return `${location.pathname}?${q}`;
  };
  // Pas d'écriture tant que le lien n'a pas été lu : sinon on effacerait ses paramètres.
  const [urlRead, setUrlRead] = useState(false);
  useEffect(() => {
    if (!urlRead) return;
    const url = configUrl(!interacted.current && !linked.current);
    if (url !== location.pathname + location.search) history.replaceState(history.state, "", url);
  });
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const hf = parseHfRepo(q.get("hf") ?? "");
    // Un lien fixe toute la configuration : ce qu'il omet reprend DEFAULTS, pas le n°1 du moment.
    linked.current = !!hf || urlFields.some(([key]) => q.has(key));
    if (linked.current) {
      // Réglages absents du lien : ceux du moteur du lien (llama.cpp : 95 % et 1 GB, pas ceux de vLLM).
      const engineDefaults = engineSettings(RUNTIMES.find((rt) => rt.id === q.get("engine"))?.id ?? DEFAULTS.engine);
      const trainingLink = (q.get("mode") ?? "inference") !== "inference" && Object.hasOwn(MODE_LABEL, q.get("mode")!);
      for (const [key, , set, valid] of urlFields) {
        const v = q.get(key);
        set(v !== null && valid(v) ? v : key === "util" || key === "oh" ? engineDefaults[key] : key === "ctx" && trainingLink ? TRAINING_CTX : DEFAULTS[key]);
      }
    }
    const linkedGpu = GPUS.find((g) => g.id === q.get("gpu"));
    if (linkedGpu && q.get("mem") === null) setVram(String(linkedGpu.vram));
    // Lien sans format de poids : celui que le site choisirait (pas de checkpoint FP8 sur un A100).
    const linkedModel = MODELS.find((m) => m.id === q.get("model"));
    if (linked.current && linkedModel && q.get("w") === null) {
      const linkedEngine = RUNTIMES.find((r) => r.id === q.get("engine"))?.id ?? DEFAULTS.engine;
      setWeightFormat(adaptFormat(linkedEngine, linkedGpu ?? GPUS.find((g) => g.id === DEFAULTS.gpu), linkedModel, linkedModel.native ? "native" : "bf16"));
    }
    if (hf) { setHfInput(hf); loadRepo(hf, "link"); }
    setUrlRead(true);
    // Lecture unique au montage : les setters et validateurs ne changent pas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Page intacte : le modèle suit le n°1 des tendances (instantané du build, puis fichier de l'heure).
  const top = trendRows[0];
  const autoKey = useRef(FIRST_ROW?.key ?? FIRST_TREND.id);
  useEffect(() => {
    if (!urlRead || !top || top.key === autoKey.current || interacted.current || linked.current) return;
    autoKey.current = top.key;
    if (top.preset) { setKvFormat(DEFAULTS.kv); onModel(top.preset.id); }
    else if (top.m.repo) void loadRepo(top.m.repo, "auto");
    // Seul le n°1 compte : onModel et loadRepo changent à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlRead, top?.key]);
  // Lien vers une question de la FAQ : la déplie avant d'y défiler (les réponses sont repliées).
  const openFaq = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(e.currentTarget.hash.slice(1));
    if (!(target instanceof HTMLDetailsElement)) return;
    e.preventDefault();
    target.open = true;
    // Sans animation, puis recalé : les sections rendues à la demande (content-visibility) prennent leur vraie
    // hauteur en chemin, et un défilement animé finissait en bas de page, la question hors de l'écran.
    // Recalé à chaque image tant que la question bouge encore (une seconde au plus).
    let last = NaN, still = 0;
    const started = performance.now();
    const go = () => {
      target.scrollIntoView({ block: "start" });
      const top = target.getBoundingClientRect().top;
      still = Math.abs(top - last) < 2 ? still + 1 : 0;
      last = top;
      if (still < 3 && performance.now() - started < 1000) requestAnimationFrame(go);
    };
    go();
    target.querySelector("summary")?.focus({ preventScroll: true });
  };
  const copyLink = () => {
    navigator.clipboard.writeText(location.origin + configUrl(false)).then(
      () => { setCopied(true); setAnnounce("Link copied to the clipboard."); },
      () => { setCopied(false); setAnnounce("Copy failed: copy the address bar instead."); },
    );
    setTimeout(() => setCopied(null), 2500);
  };

  // Rapport : page HTML autonome (aux couleurs du site, imprimable en PDF) avec sa version
  // Markdown pour un ticket ou une IA. Les chiffres viennent d'ici, la mise en page de vramReport.ts.
  const downloadReport = async () => {
    const kvLabel = KV_FORMATS.find((f) => f.id === kvFormat)?.label ?? kvFormat;
    const abs = (path: string) => location.origin + path;
    const ours = MODELS.includes(model);
    // Drapeau intégré au fichier : le rapport s'affiche aussi hors ligne.
    const cc = modelLab?.country?.toLowerCase();
    // Pays inconnu : le drapeau neutre, comme sur le site.
    const flagSvg = modelLab ? await fetch(cc && /^[a-z]{2}$/.test(cc) ? `/flags/${cc}.svg` : "/flag-unknown.svg").then((res) => (res.ok ? res.text() : "")).catch(() => "") : "";
    const rw = runsWith(engines);
    const runsWithText = rw && (rw.names.length ? rw.names.join(", ") : "Not yet in vLLM or llama.cpp") +
      (rw.viaCard ? (rw.setup ? ` (vLLM after ${rw.setup}, as the model card says)` : " (vLLM with the setup on the model card)") : "") +
      (rw.gguf ? (rw.gguf.runs ? ` (llama.cpp with the GGUF build ${rw.gguf.repo})` : `. GGUF builds such as ${rw.gguf.repo} need a patched llama.cpp (${rw.gguf.arch} is not in mainline yet)`) : "");
    const data: ReportData = {
      generated: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
      configUrl: abs(configUrl(false)),
      model: {
        name: nameOf(model),
        isNew: ours && isNewModel(model, pricesDate),
        origin: modelLab && {
          lab: modelLab.name,
          country: modelLab.country && countryName(modelLab.country),
          flag: flagSvg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(flagSvg)}` : undefined,
        },
        parent: parentPreset && { label: modelName(parentPreset), url: abs(modelPath(parentPreset)) },
        facts: [
          `${billions(model.params)}B parameters${model.activeParams ? ` (${model.activeParams}B active)` : ""}`,
          ...(Number.isFinite(model.maxContext) ? [`${tokens(model.maxContext)} context`] : []),
          ...(modelLicense ? [`${modelLicense.label}${modelLicense.permissive ? ", commercial use allowed" : ""}`] : []),
          ...(model.released ? [`${model.preview ? "Announced" : "Released"} ${longDay(model.released)}`] : []),
        ],
        arch: model.arch,
        preview: model.preview?.note,
        popularity: [
          entry?.hf && `${compact(entry.hf.likes)} likes and ${compact(entry.hf.downloads)} downloads last month on Hugging Face`,
          entry?.ollama && `${compact(entry.ollama.pulls)} pulls on Ollama`,
        ].filter(Boolean).join(" · ") || undefined,
        // Moteurs d'inférence et commande Ollama : hors sujet dans un rapport d'entraînement (pas de section « Run it »).
        runsWith: training ? undefined : runsWithText,
        ollama: !training && entry?.ollama ? ollamaCommand(entry.ollama) : undefined,
        links: [
          ...(ours ? [{ label: "Model card", url: abs(modelPath(model)) }] : []),
          ...(model.repo ? [{ label: "Hugging Face", url: `https://huggingface.co/${model.repo}` }] : []),
          ...(entry?.modelscope ? [{ label: "ModelScope", url: `https://modelscope.cn/models/${entry.modelscope}` }] : []),
          ...(engines?.recipe ? [{ label: "vLLM recipe", url: engines.recipe }] : []),
          ...(model.preview ? [{ label: "Announcement", url: model.preview.url }] : []),
        ],
      },
      answer: {
        status: statusClass, statusLabel, headline,
        cost: cost && offers.length ? `From $${usd2(r.gpus * offers[0].price)}/hour on ${offers[0].name.split(" (")[0]}` : undefined,
        lines: [
          ...(splitText && !training ? [`Split as ${splitText}.`] : []),
          `${(r.gpus * nVram).toLocaleString("en-US")} GB of GPU memory in total.`,
          ...(neverFits ? [`More GPUs do not help: ${neverFitsWhy}.`] : autoGpus === null ? ["Does not fit on 128 of these GPUs."] : autoGpus !== r.gpus ? [`Fewest GPUs that fit: ${autoGpus}.`] : []),
          ...(betterQuant ? [`With ${formatLabel(betterQuant.f.id)} weights it fits on ${target} (${quality(betterQuant.f.id, betterQuant.f.bits).toLowerCase()}).`] : []),
          ...(offload?.plan ? [`With llama.cpp and ${ramText} GB of system RAM: ${gib(offload.plan.vramGiB)} on ${offloadGpus} × ${gpuShort}, ${gib(offload.plan.ramGiB)} in RAM (${offload.plan.flag}), about ${tps(offload.plan.tokensPerSecond)} tokens/s.`] : []),
          ...hwWarnings.map((w) => w.detail),
        ],
      },
      memory: { parts: segments.map((sg) => ({ key: sg.key, label: sg.label, gib: sg.value })), needed: r.totalGiB, usable: r.budgetGiB, free: r.freeGiB },
      speed: speed ? {
        tiles: [
          { value: `${tps(speed.perRequest)} tok/s`, label: "generated per request" },
          { value: `${tps(speed.total)} tok/s`, label: `in total, ${nSeqText} requests at full context` },
          ...(ttft !== null ? [{ value: secs(ttft), label: `to the first token of a ${tokens(ctx)}-token prompt` }] : []),
          ...(economics ? [{ value: `$${usd(economics.selfPer1M)}`, label: "your cost per 1M generated tokens, full load" }] : []),
          ...(api ? [{ value: `$${api.output}`, label: `per 1M output tokens via API (${api.provider})` }] : []),
        ],
        lines: [
          `Generation: about ${tps(speed.perRequest)} tokens/s per request, ${tps(speed.total)} tokens/s in total (${nSeqText} requests, full context)`,
          ...(ttft !== null ? [`First token after a ${tokens(ctx)}-token prompt: about ${secs(ttft)}`] : []),
          ...(economics ? [`Your cost: $${usd(economics.selfPer1M)} per 1M generated tokens at full load`] : []),
          ...(api ? [`Same model via API (${api.provider}, cheapest of ${api.offers}): $${api.input} input, $${api.output} output per 1M tokens`] : []),
        ],
        verdict: economics && api ? (economics.utilizationToBreakEven <= 1
          ? `Self-hosting is cheaper than the API above ${Math.round(economics.utilizationToBreakEven * 100)}% GPU utilization (about ${bigCount(economics.breakEvenTokensPerDay)} tokens/day)`
          : "The API is cheaper even at full load") : undefined,
      } : undefined,
      setup: [
        ["Model", labelOf(model)],
        ["Workload", MODE_LABEL[mode]],
        ...(training ? [] : [["Weights", weightLabel], ["KV cache", kvLabel]] as [string, string][]),
        [training ? "Sequence length" : "Context per request", `${tokens(ctx)} tokens`],
        [training ? "Micro-batch per GPU" : "Concurrent requests", String(nSeq)],
        ["GPU", gpuName],
        ["Number of GPUs", `${r.gpus}${gpuCount === "auto" ? " (auto)" : ""}`],
        ...(training ? [] : [["Serving engine", `${runtime.label}, ${nUtil}% usable, ${nOverhead} GiB overhead per GPU`]] as [string, string][]),
      ],
      capacity: training ? undefined : [
        `Max concurrent requests at ${tokens(ctx)} tokens: ${r.maxSequences.toLocaleString("en-US")}`,
        `Max context with ${nSeqText} request${nSeq === 1 ? "" : "s"}${withRam ? " (GPU memory only)" : ""}: ${r.maxContext >= model.maxContext ? `${tokens(model.maxContext)} (model max)` : r.maxContext > 0 ? tokens(r.maxContext) : "does not fit"}`,
        `KV cache per request: ${bytes(kvPerSeq)} (${bytes(kvBytesPerToken(model, ctx, kvBytes))} per token)`,
        ...(model.stateBytesPerSeq ? [`Recurrent state per request: ${bytes(model.stateBytesPerSeq)}`] : []),
      ],
      quant: training ? undefined : {
        title: `Quantization on ${gpuName}`,
        rows: quantRows.map(({ f, size, n }): [string, string, string, string] => [formatLabel(f.id), quality(f.id, f.bits), gib(size / GiB), n === null ? "over 128" : String(n)]),
      },
      options: options.map((o): [string, string, string] => [`${o.n} × ${gpuName0(o.g)}`, Number.isFinite(o.cost) ? `$${usd2(o.cost)}/hour` : "no price yet", o.provider ?? ""]),
      rent: offers.length && r.status !== "oom" ? {
        date: pricesStamp,
        rows: offers.map((o) => ({
          name: o.name.split(" (")[0], price: `$${usd2(r.gpus * o.price)}/hour for ${r.gpus} GPU${r.gpus > 1 ? "s" : ""}`,
          url: rentUrl(o), sponsored: !!REFERRAL_LINKS[o.id],
        })),
      } : undefined,
      deploy: training ? undefined : { engine: DEPLOY_ENGINES.find((e) => e.id === deployTab)!.label, command },
    };
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([reportHtml(data)], { type: "text/html;charset=utf-8" }));
    a.download = `vram-report-${nameOf(model).toLowerCase().replace(/[^a-z0-9.]+/g, "-")}.html`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const onGpu = (id: string) => {
    setGpuId(id);
    const g = GPUS.find((x) => x.id === id);
    if (!g) return;
    setVram(String(g.vram));
    if (training) { if (!g.fp8 && kvFormat === "fp8") setKvFormat("bf16"); return; }
    // Moteur de ce type de carte sauf choix du visiteur, poids équivalents (FP8 → INT8 sur un A100).
    apply(setupAfterGpu(model, setup, g, engineChosen.current));
  };
  const onRuntime = (id: string) => (training ? applyEngine(id) : apply(setupAfterEngine(model, setup, id)));
  // Format choisi par le visiteur : s'il faut un autre moteur pour le charger (GGUF → llama.cpp), on
  // y passe ; sinon (FP8 sur Mac, où vLLM ne tourne pas) son équivalent pour ce moteur.
  const weightNote = (id: string) => {
    const e = engineFor(id, model, gpu, runtimeId);
    if (!e) return " (not on this GPU)";
    if (e !== runtimeId) return ` (${e === "llamacpp" ? "llama.cpp" : "vLLM"})`;
    return e === "llamacpp" || runsNatively(gpu, model, id) ? "" : ` (no ${formatNeeds(model, id)!.toUpperCase()} on this GPU)`;
  };
  const onWeights = (id: string) => {
    interacted.current = true;
    const next = setupAfterWeights(model, setup, id);
    if (next.engine !== runtimeId) engineChosen.current = true; // le format impose le moteur : un changement de GPU le garde
    apply(next);
  };
  // Combinaisons impossibles, d'où qu'elles viennent (GPU changé, lien partagé) : un moteur absent
  // de cette plateforme (vLLM sur Mac), un cache KV que le moteur ne sait pas allouer (Q4_0 avec vLLM).
  useEffect(() => {
    if (!urlRead) return;
    const next = coherentSetup(model, setup);
    apply(training ? { ...next, weights: wf } : next);
    // apply change à chaque rendu ; seules ces valeurs comptent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlRead, gpuId, runtimeId, kvFormat, wf, modelId]);
  // Retour à l'état d'arrivée sur la page : le n°1 des tendances sur un H100, réglages par défaut,
  // adresse nue.
  const reset = () => {
    interacted.current = false;
    engineChosen.current = false;
    linked.current = false;
    hfPending.current = null;
    setMode("inference"); setCustom(CUSTOM_START);
    setContext(DEFAULTS.ctx); setSequences(DEFAULTS.req);
    setGpuId(DEFAULTS.gpu); setVram(DEFAULTS.mem); setGpuCount(DEFAULTS.n); setTp(DEFAULTS.tp); setPp(DEFAULTS.pp);
    applyEngine(DEFAULTS.engine);
    setGradCkpt(true); setShardStates(true); setLoraPct(DEFAULTS.lora); setRamGb(DEFAULTS.ram); setRamTypeId(DEFAULTS.ramt); setDisplay(false);
    setHfModel(null); setHfInput(""); setHfState({});
    const first = top?.preset ?? (top?.m.repo ? undefined : FIRST_TREND);
    if (first) {
      setModelId(first.id);
      setWeightFormat(first.native ? "native" : "bf16");
      setKvFormat(first.fp8Kv ? "fp8" : DEFAULTS.kv);
    } else {
      setWeightFormat("bf16"); // le chargement passe au checkpoint officiel s'il en a un (onModel)
      setKvFormat(DEFAULTS.kv);
      void loadRepo(top!.m.repo!, "auto");
    }
    setAnnounce("Settings reset.");
  };

  // Explication du calcul : sous les réglages en inférence (la colonne de gauche est la
  // plus courte), sous les résultats en entraînement.
  // Étapes des résultats : ① la réponse, ② les options pour moins de matériel, ③ louer et déployer.
  const hasOptions = quantRows.length > 1 || options.length > 0 || matrix.length > 0 || otherModels.length > 0;
  const hasPrices = !!gpu && PRICED_GPUS.includes(gpu);
  // Étape « options » : le titre suit la situation. Avec un seul GPU, « moins de matériel »
  // n'a pas de sens : les leviers sont un format plus fidèle ou une carte moins chère.
  // Entraînement : pas de format de poids à choisir (quantRows vide), seulement d'autres GPU.
  const optionsTier = training
    ? r.status === "oom"
      ? { title: "Make it fit", desc: "Switch to a GPU with more memory. Click an option to apply it." }
      : { title: "A cheaper GPU?", desc: "Other GPUs that fit this training run. Click an option to apply it." }
    : r.status === "oom"
    ? { title: "Make it fit", desc: "Quantize the weights or switch to a GPU with more memory. Click an option to apply it." }
    : r.gpus <= 1
      ? { title: "Better quality or a cheaper GPU?", desc: "Pick a more faithful weight format (it may need more GPUs) or a cheaper card. Click an option to apply it." }
      : { title: "Need less hardware?", desc: "Quantize the weights or switch to a cheaper GPU. Click an option to apply it." };
  // Électricité à la maison : GPU qu'on peut avoir chez soi, configuration qui tient, prix Eurostat chargés.
  const homePower = gpu && !isDatacenter(gpu) && r.status !== "oom" ? prices.electricity : undefined;
  const home = homePower ? "the electricity to run it at home" : "";
  const rentTier = hasPrices && !training
    ? { title: "Rent and deploy", desc: `Today's ${gpuShort} prices per provider, ${home ? `${home}, ` : ""}then the command to launch it.` }
    : hasPrices
      ? { title: "Rent the GPUs", desc: `Today's ${gpuShort} prices per provider${home ? `, and ${home}` : ""}.` }
      : { title: "Launch it", desc: home ? `The electricity to run it at home, then the command for your serving engine.` : "The command for your serving engine." };
  // Encart vers /gpu-prices : quelques autres GPU courants avec leur prix du jour, pour inviter à comparer.
  const promoGpus = ["h100", "h200", "b200", "rtx-pro-6000", "rtx-4090", "rtx-5090", "mi300x", "l40s"]
    .filter((id) => id !== gpuId)
    .flatMap((id) => {
      const g = PRICED_GPUS.find((x) => x.id === id);
      const best = offersFor(prices, id)[0];
      return g && best ? [{ g, price: best.price }] : [];
    })
    .slice(0, 4);


  return (
    <>
      <SeoHead {...ROUTE_SEO["/llm-vram-calculator"]} />
      <Seo
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "https://studiotvai.com/#website",
              name: "StudioTV",
              url: "https://studiotvai.com/",
              inLanguage: "en",
            },
            {
              "@type": "WebPage",
              "@id": "https://studiotvai.com/llm-vram-calculator",
              name: "LLM VRAM Calculator",
              description: ROUTE_SEO["/llm-vram-calculator"].description,
              url: "https://studiotvai.com/llm-vram-calculator",
              inLanguage: "en",
              dateModified: ROUTE_SEO["/llm-vram-calculator"].lastmod,
              isPartOf: { "@id": "https://studiotvai.com/#website" },
              primaryImageOfPage: { "@type": "ImageObject", url: "https://studiotvai.com/og-cover.png" },
              mainEntity: { "@id": "https://studiotvai.com/llm-vram-calculator#app" },
            },
            {
              "@type": "SoftwareApplication",
              "@id": "https://studiotvai.com/llm-vram-calculator#app",
              name: "LLM VRAM Calculator",
              description:
                "GPU memory calculator for 2026 LLMs: weights, KV cache for sliding-window, linear-attention, MLA and compressed-attention models, recurrent state, tensor and pipeline parallelism, and fine-tuning.",
              url: "https://studiotvai.com/llm-vram-calculator",
              applicationCategory: "DeveloperApplication",
              operatingSystem: "Any (web-based)",
              isAccessibleForFree: true,
              offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
              image: "https://studiotvai.com/og-cover.png",
              creator: { "@type": "Organization", "@id": "https://studiotvai.com/#organization", name: "StudioTV" },
              featureList: [
                "Presets from official configs: Qwen 3.5–3.8, Qwen Coder, Gemma 4, gpt-oss, DeepSeek V4 and R1, Kimi K3 and K2.6, GLM-5.3, Nemotron 3, MiniMax M2.7, Llama 3 and 4",
                "Exact KV cache for sliding-window, hybrid linear-attention, MLA and compressed attention",
                "Native FP8 / MXFP4 checkpoints, NVFP4, GPTQ/AWQ and GGUF weight formats, FP8 KV cache",
                "Minimum GPU count, and the most accurate quantization that fits a given number of GPUs",
                "Rental prices from RunPod, Verda, Vast.ai and Azure, checked every hour, and the cheapest GPUs that fit",
                "Maximum concurrency and context that fit",
                "Shareable links for every configuration",
                "Full fine-tuning, LoRA and QLoRA memory",
              ],
              keywords: [
                "LLM VRAM calculator", "GPU memory calculator", "KV cache calculator", "vLLM", "SGLang",
                "llama.cpp", "tensor parallelism", "quantization", "NVFP4", "context length",
                "Qwen", "Gemma 4", "gpt-oss", "DeepSeek V4", "Kimi K3",
              ],
            },
            {
              "@type": "FAQPage",
              "@id": "https://studiotvai.com/llm-vram-calculator#faq",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: faqText(f) },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://studiotvai.com/" },
                { "@type": "ListItem", position: 2, name: "LLM VRAM Calculator", item: "https://studiotvai.com/llm-vram-calculator" },
              ],
            },
          ],
        }}
      />
      <main id="main" className="vram-page">
        <section className="vram-hero">
          <h1 className="vram-title reveal-now">LLM VRAM Calculator</h1>
          <p className="vram-subtitle reveal-now">
            How much VRAM and how many GPUs your LLM needs, with the real KV cache of today's architectures.
            Pick a model or paste any Hugging Face link.
          </p>
          {/* Libellé hors de la rangée de puces : sur mobile, il passe au-dessus et la rangée défile seule. */}
          <div className="vram-chips-row reveal-now">
            <span className="vram-chips-label" id="vram-chips-label">{trendRows.length ? "Trending on Hugging Face" : "Latest releases"}</span>
            <div className="vram-chips" role="group" aria-labelledby="vram-chips-label">
            {picks.map((p) => {
              const lab = p.lab;
              const pressed = p.preset ? modelId === p.preset.id : modelId === HF_ID && hfModel?.repo === p.repo;
              return (
                <button
                  key={p.repo || p.label} type="button" className="vram-chip" aria-pressed={pressed}
                  onClick={() => (p.preset ? quickPick(p.preset.id) : sizeRepo(p.repo))}
                >
                  <Flag country={lab?.country} />
                  {p.label}
                </button>
              );
            })}
            </div>
          </div>
          <p className="vram-disclaimer reveal-now" role="note">
            <strong>Estimates, not guarantees.</strong>
            <span className="vram-disclaimer-long"> Real usage depends on your engine and settings:
            keep some headroom and confirm with <code>nvidia-smi</code>.</span>
            <span className="vram-disclaimer-short"> Confirm with <code>nvidia-smi</code>.</span>
          </p>
        </section>

        <ErrorBoundary fallback={() => (
          <section className="vram-console">
            <div className="vram-console-inner vram-crash" role="alert">
              <h2 className="vram-block-title vram-panel-title">The calculator hit an unexpected error</h2>
              <p className="vram-note">These settings triggered a bug. Resetting restores the default configuration; the rest of the page still works.</p>
              <a className="vram-cta" href="/llm-vram-calculator">Reset the calculator</a>
            </div>
          </section>
        )}>
        <section className="vram-console" id="vram-calculator" tabIndex={-1}
          onChangeCapture={() => { interacted.current = true; }} onClickCapture={() => { interacted.current = true; }}>
          <div className="vram-console-inner">
            <header className="vram-console-header">
              <div>
                <div className="vram-console-eyebrow">Estimator</div>
                <div className="vram-console-title">{labelOf(model)}</div>
                {autoRepo && !interacted.current && (
                  <div className="vram-console-loading" role="status">
                    Loading {tidyLabel(prettyModelName(autoRepo.split("/").pop() ?? autoRepo), autoRepo)}, #1 trending on Hugging Face…
                  </div>
                )}
              </div>
              <div className="vram-console-meta">
                <span className="vram-meta-chip">{MODE_LABEL[mode]}</span>
                {!training && <span className="vram-meta-chip">{runtime.label}</span>}
                <span className="vram-meta-chip">{r.gpus.toLocaleString("en-US")} × {nVram.toLocaleString("en-US")} GB</span>
                <div className="vram-console-actions">
                <button type="button" className="vram-share vram-share-icon" onClick={reset}>
                  <RotateCcw size={14} aria-hidden="true" /><span className="vram-share-text">Reset</span>
                </button>
                <button type="button" className="vram-share vram-share-icon" onClick={copyLink}>
                  {copied ? <Check size={14} aria-hidden="true" /> : <Link2 size={14} aria-hidden="true" />}
                  <span className="vram-share-text">{copied === null ? "Copy link" : copied ? "Link copied" : "Copy the address bar"}</span>
                </button>
                <button type="button" className="vram-share vram-share-icon" onClick={() => void downloadReport()}>
                  <Download size={14} aria-hidden="true" /><span className="vram-share-text">Download report</span>
                </button>
                {MODELS.includes(model) && (
                  <Link className="vram-share vram-share-icon" to={modelPath(model)}>
                    <FileText size={14} aria-hidden="true" /><span className="vram-share-text">Model card</span>
                  </Link>
                )}
                </div>
              </div>
            </header>

            <div className="vram-console-body">
              <div className="vram-console-grid">
                <div className="vram-input-column">
                  <TierHead n={1} title="Your setup" desc="Pick a model, the context and your GPUs." />
                  <section className="vram-block">
                    <h2 className="vram-block-title">Model</h2>
                    <div className="vram-field-stack">
                      <Field id="model" label="Pick a model">
                        <ModelPicker
                          id="model" value={modelId} models={MODELS} groupOrder={modelGroups}
                          extra={hfPick} extraTitle="From Hugging Face" customId={CUSTOM_ID} customLabel="Custom dense model"
                          labelOf={(m) => (m.id === HF_ID && hfModel ? labelOf(hfModel) : labelOf(m))}
                          labOf={(m) => labOf(m, catalog)} downloadsOf={(m) => catalogEntry(m)?.hf?.downloads ?? 0}
                          eciOf={(m) => catalogEntry(m)?.eci?.score} isNew={(m) => isNewModel(m, pricesDate)}
                          fitOf={training ? undefined : (m) => modelFit({ ...base, model: m }, gpu, runtimeId, pickerGpus, exactOf, gridRam)}
                          fitKey={`${gpuId}|${runtimeId}|${pickerGpus}|${ctx}|${nSeq}|${kvFormat}|${memGiB}|${nUtil}|${nOverhead}|${nRam}|${ramTypeId}|${catalog.updated}`}
                          fitBasis={`4-bit (${runtimeId === "llamacpp" ? "Q4_K_M" : "INT4 GPTQ / AWQ"}), ${tokens(ctx)} tokens, ${nSeqText} request${nSeq === 1 ? "" : "s"}`}
                          gpuName={gpuShort} gpuCount={pickerGpus} onChange={onModel}
                        />
                      </Field>
                      <form className="vram-control" onSubmit={onHfSubmit}>
                        <div className="vram-label">
                          <label htmlFor="hf">Or any Hugging Face model</label>
                          <Info label="Hugging Face model" text="Paste a link or org/model: reads the model's config.json and safetensors metadata straight from Hugging Face." />
                        </div>
                        <div className="vram-inline">
                          <input
                            id="hf" type="text" className="vram-input" inputMode="url" autoComplete="off" spellCheck={false}
                            autoCapitalize="off" autoCorrect="off" enterKeyHint="go"
                            placeholder="org/model or link" value={hfInput}
                            onChange={(e) => setHfInput(e.target.value)}
                          />
                          <button type="submit" className="vram-action" disabled={hfState.loading}>
                            {hfState.loading ? "Loading…" : "Load"}
                          </button>
                        </div>
                        {hfState.error && <p className="vram-note vram-warn" role="alert">{hfState.error}</p>}
                      </form>
                      {modelId === CUSTOM_ID ? (
                        <>
                          <div className="vram-field-grid vram-grid-3">
                            <Field id="c-params" label="Params (B)"><NumberInput id="c-params" used={customUsed.params} value={custom.params} onChange={(v) => setCustom({ ...custom, params: v })} step={0.1} /></Field>
                            <Field id="c-layers" label="Layers" info="num_hidden_layers in config.json"><NumberInput id="c-layers" used={customUsed.layers} value={custom.layers} onChange={(v) => setCustom({ ...custom, layers: v })} /></Field>
                            <Field id="c-kv" label="KV heads" info="num_key_value_heads in config.json"><NumberInput id="c-kv" used={customUsed.kvHeads} value={custom.kvHeads} onChange={(v) => setCustom({ ...custom, kvHeads: v })} /></Field>
                            <Field id="c-hd" label="Head dim" info="head_dim, or hidden_size ÷ num_attention_heads"><NumberInput id="c-hd" used={customUsed.headDim} value={custom.headDim} onChange={(v) => setCustom({ ...custom, headDim: v })} /></Field>
                            <Field id="c-hidden" label="Hidden size" info="Only used for training activations"><NumberInput id="c-hidden" used={customUsed.hidden} value={custom.hidden} onChange={(v) => setCustom({ ...custom, hidden: v })} /></Field>
                            <Field id="c-vocab" label="Vocab size" info="Only used for training logits"><NumberInput id="c-vocab" used={customUsed.vocab} value={custom.vocab} onChange={(v) => setCustom({ ...custom, vocab: v })} /></Field>
                          </div>
                          <p className="vram-note">Assumes full attention with GQA on every layer. For sliding-window, hybrid or MLA models, start from the closest preset.</p>
                        </>
                      ) : (
                        <>
                          {/* Aperçu de la fiche du modèle (toutes les sources réunies), et le chemin pour l'ouvrir.
                              Un modèle importé n'a pas de fiche : sa page Hugging Face. Un fine-tune renvoie aussi
                              à la fiche du modèle d'origine. */}
                          <div className="vram-model-card">
                            {(modelLab || parentPreset) && (
                              <p className="vram-model-card-origin">
                                {modelLab && <><Flag country={modelLab.country} />{modelLab.name}{modelLab.country ? `, ${countryName(modelLab.country)}` : ""}</>}
                                {modelLab && parentPreset && " · "}
                                {parentPreset && <>fine-tune of <Link className="vram-link" to={modelPath(parentPreset)}>{modelName(parentPreset)}</Link></>}
                              </p>
                            )}
                            <p className="vram-model-card-facts">
                              {[
                                `${billions(model.params)}B parameters${model.activeParams ? ` (${model.activeParams}B active)` : ""}`,
                                Number.isFinite(model.maxContext) && `${tokens(model.maxContext)} context`,
                                modelLicense?.label,
                              ].filter(Boolean).join(" · ")}
                            </p>
                            <p className="vram-note">{model.arch}</p>
                            {MODELS.includes(model) ? (
                              <div className="vram-model-card-actions">
                                <Link className="vram-model-card-cta" to={modelPath(model)}>
                                  Open the {modelName(model)} model card<ArrowRight size={16} aria-hidden="true" />
                                </Link>
                                {model.preview && (
                                  <a className="vram-link" href={model.preview.url} target="_blank" rel="noopener noreferrer">
                                    Announcement<span className="sr-only"> (opens in a new tab)</span>
                                  </a>
                                )}
                              </div>
                            ) : (
                              <div className="vram-model-card-actions">
                                <a className="vram-model-card-cta" href={`https://huggingface.co/${model.repo}`} target="_blank" rel="noopener noreferrer">
                                  View on Hugging Face<ArrowUpRight size={16} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
                                </a>
                              </div>
                            )}
                          </div>
                          {model.preview && <p className="vram-note vram-warn">{model.preview.note}</p>}
                        </>
                      )}
                      <div className="vram-field-grid vram-grid-2">
                        <Field id="mode" label="Workload">
                          <select id="mode" className="vram-select" value={mode} onChange={(e) => {
                            // Séquence par défaut de l'entraînement (4K) au lieu du contexte de l'inférence (32K), et retour.
                            const next = e.target.value as Mode;
                            if (mode === "inference" && next !== "inference" && context === DEFAULTS.ctx) setContext(TRAINING_CTX);
                            if (mode !== "inference" && next === "inference" && context === TRAINING_CTX) setContext(DEFAULTS.ctx);
                            setMode(next);
                          }}>
                            <option value="inference">Inference</option>
                            <option value="full">Full fine-tune (AdamW)</option>
                            <option value="lora">LoRA</option>
                            <option value="qlora">QLoRA (4-bit base)</option>
                          </select>
                        </Field>
                        {!training && (
                          <Field id="wfmt" label="Weights" info="Effective bits per weight, quantization scales included. Native = exact size of the official checkpoint files.">
                            <select id="wfmt" className="vram-select" value={wf} onChange={(e) => onWeights(e.target.value)}>
                              {WEIGHT_FORMATS.filter((f) => f.id !== "native" || model.native).map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.id === "native" ? model.native?.label : f.label}
                                  {weightNote(f.id)}
                                </option>
                              ))}
                            </select>
                          </Field>
                        )}
                      </div>
                      {exactNow && (
                        <p className="vram-note">
                          Exact size of the {exactNow.quant} files in{" "}
                          <a className="vram-link" href={`https://huggingface.co/${exactNow.repo}`} target="_blank" rel="noopener noreferrer">
                            {exactNow.repo}<span className="sr-only"> (opens in a new tab)</span>
                          </a>: {gib(exactNow.bytes / GiB)}.
                        </p>
                      )}
                    </div>
                  </section>

                  <section className="vram-block">
                    <h2 className="vram-block-title">{training ? "Training batch" : "Requests"}</h2>
                    <div className="vram-field-stack">
                      <div className="vram-field-grid vram-grid-3">
                        <Field id="ctx" label={training ? "Sequence length" : "Context per request (tokens)"} info={training ? "Tokens per training sample" : "Prompt + generated tokens"}>
                          <NumberInput id="ctx" used={ctx} value={context} onChange={setContext} step={1024} />
                        </Field>
                        <Field id="seqs" label={training ? "Micro-batch per GPU" : "Concurrent requests"} info={training ? "Samples processed at once on each GPU" : "Requests decoded at the same time; each holds its own KV cache"}>
                          <NumberInput id="seqs" used={nSeq} value={sequences} onChange={setSequences} />
                        </Field>
                        {!training && (
                          <Field id="kvfmt" label="KV cache" info="Set independently of the weight format. FP8 halves the cache.">
                            <select id="kvfmt" className="vram-select" value={kvFormat} onChange={(e) => setKvFormat(e.target.value)}>
                              {KV_FORMATS.map((f) => (
                                <option key={f.id} value={f.id} disabled={!kvRuns(runtimeId, f.id)}>
                                  {f.label}{kvRuns(runtimeId, f.id) ? "" : ` (not with ${runtime.label.split(" /")[0]})`}
                                </option>
                              ))}
                            </select>
                          </Field>
                        )}
                      </div>
                      {ctx > model.maxContext && (
                        <p className="vram-note vram-warn">Longer than this model's maximum context ({tokens(model.maxContext)} tokens).</p>
                      )}
                      {training && (
                        <div className="vram-checks">
                          <label className="vram-checkbox"><input type="checkbox" checked={gradCkpt} onChange={(e) => setGradCkpt(e.target.checked)} /> Gradient checkpointing</label>
                          <label className="vram-checkbox"><input type="checkbox" checked={shardStates} onChange={(e) => setShardStates(e.target.checked)} /> Shard across GPUs (FSDP / ZeRO-3)</label>
                        </div>
                      )}
                      {(mode === "lora" || mode === "qlora") && (
                        <div className="vram-field-grid vram-grid-3">
                          <Field id="lora" label="Trainable params (%)" info="LoRA rank 16 on every linear layer is typically 0.3–1% of the model">
                            <NumberInput id="lora" used={nLora} value={loraPct} onChange={setLoraPct} step={0.1} />
                          </Field>
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="vram-block">
                    <h2 className="vram-block-title">Hardware</h2>
                    <div className="vram-field-stack">
                      <Field id="gpu" label="GPU">
                        <select id="gpu" className="vram-select" value={gpuId} onChange={(e) => onGpu(e.target.value)}>
                          {gpuGroups.map((g) => (
                            <optgroup key={g} label={g}>
                              {GPUS.filter((x) => x.group === g).map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                            </optgroup>
                          ))}
                          <option value="custom">Other…</option>
                        </select>
                      </Field>
                      <div className="vram-detect">
                        <button type="button" className="vram-share" onClick={() => {
                          const renderer = readRenderer();
                          const d = renderer ? detectGpu(renderer) : "hidden";
                          setDetected(d);
                          if (d !== "hidden" && d.kind === "gpu") { onGpu(d.id); setGpuCount("auto"); }
                          setAnnounce(d === "hidden" ? "Your browser hides its GPU." : d.kind === "gpu" ? `${d.name} selected.` : "GPU checked.");
                        }}>Detect my GPU</button>
                        {detected && (
                          <p className="vram-note" role="status">
                            {detected === "hidden"
                              ? "Your browser hides the name of its GPU (Brave, Safari or a privacy setting): pick it in the list."
                              : detected.kind === "gpu" ? <>Found your {detected.name}: selected.</>
                                : detected.kind === "memory" ? <>Found an {detected.chip}. The browser can't read its memory, pick yours:{" "}
                                  {detected.ids.map((id) => {
                                    const g = GPUS.find((x) => x.id === id)!;
                                    return <button key={id} type="button" className={`vram-tab${gpuId === id ? " active" : ""}`} aria-pressed={gpuId === id}
                                      onClick={() => { onGpu(id); setGpuCount("auto"); }}>{/(\d+) GB/.exec(g.label)?.[1]} GB</button>;
                                  })}</>
                                  : detected.kind === "family" ? <>Your browser only reports the family ({detected.family}), not the exact model (Firefox does this to limit tracking): pick yours in the list, or open this page in Chrome or Edge to detect it.</>
                                  : detected.kind === "unlisted" ? <>Found your {detected.name}, not in the list yet: pick a card with the same memory, or Other… and type it.</>
                                    : <>Your browser reports {detected.name}, a GPU built into the processor: pick a dedicated card if you have one.</>}
                            {" "}Read by your browser, nothing is sent.
                          </p>
                        )}
                      </div>
                      <div className="vram-field-grid vram-grid-3">
                        <Field id="vram" label="Memory per GPU (GB)" info="As marketed. The calculation uses what the driver reports when it differs (an L40S sold as 48 GB exposes 44.99 GiB). Unified-memory presets already exclude what the OS keeps.">
                          <NumberInput id="vram" used={nVram} value={vram} onChange={(v) => { setVram(v); setGpuId("custom"); }} />
                        </Field>
                        <Field
                          id="count" label="Number of GPUs"
                          info={consoleGpu ? "One console: llama.cpp cannot split a model across several PlayStation 5s."
                            : oneBox ? "One Mac: Transformers cannot split a model across several Macs (llama.cpp can, over RPC)."
                            : training
                            ? "Data-parallel GPUs. With sharding on, weights, gradients and optimizer states are split across them."
                            : "Auto picks the fewest GPUs that fit: tensor parallel inside a server (up to 8), pipeline parallel across servers."}
                        >
                          <select id="count" className="vram-select" disabled={oneBox} value={oneBox ? "1" : manual || gpuCount !== "manual" ? gpuCount : "auto"} onChange={(e) => {
                            // Manuel : on part de la répartition actuelle plutôt que de 1 × 1.
                            if (e.target.value === "manual") { setTp(String(inputs.tp)); setPp(String(inputs.pp)); }
                            setGpuCount(e.target.value);
                          }}>
                            <option value="auto">Auto: fewest that fit ({autoGpus ?? (neverFits ? "none" : "over 128")})</option>
                            {GPU_COUNTS.map((n) => <option key={n} value={n}>{n}</option>)}
                            {!training && <option value="manual">Manual (tensor × pipeline)</option>}
                          </select>
                        </Field>
                        {manual && (
                          <>
                            <Field id="tp" label="Tensor parallel" info="Splits weights and GQA KV heads across GPUs. Past the number of KV heads, heads are replicated; MLA latents are replicated on every GPU.">
                              <NumberInput id="tp" used={manualTp} value={tp} onChange={setTp} min={1} />
                            </Field>
                            <Field id="pp" label="Pipeline parallel" info="Splits layers, and their KV cache, across GPUs">
                              <NumberInput id="pp" used={manualPp} value={pp} onChange={setPp} min={1} />
                            </Field>
                          </>
                        )}
                      </div>
                      {!training && gpu && !isDatacenter(gpu) && !isUnified(gpu) && (
                        <div className="vram-checks">
                          <label className="vram-checkbox">
                            <input type="checkbox" checked={display} onChange={(e) => { interacted.current = true; setDisplay(e.target.checked); }} />
                            {" "}This GPU also drives my screen (+{DISPLAY_GIB} GB)
                          </label>
                          <Info label="Screen on this GPU" text="A desktop and a browser take 0.5 to 1.5 GB on the card that drives the screen. On Windows, an overflow then spills into shared system memory and generation slows down sharply instead of failing; on Linux, loading fails." />
                        </div>
                      )}
                      {/* Ryzen AI Max sous Linux (doc ROCm « Strix Halo system optimization », octobre 2026) : pas de mémoire
                          dédiée, le GPU prend ce que le noyau le laisse mapper (GTT), la moitié de la RAM par défaut. Le chiffre
                          du préréglage est le maximum de Windows et du BIOS (75 %). */}
                      {gpu && isUnified(gpu) && isAmdRdna(gpu) && (() => {
                        const ram = Number(/(\d+) GB ·/.exec(gpu.label)?.[1]) || 0;
                        return (
                          <p className="vram-note">
                            {gpu.vram} GB is what Windows (or the BIOS) can reserve for the GPU. On Linux the default limit is half
                            the RAM, about {Math.round(ram / 2)} GB; AMD's amd-ttm tool raises it. Type your limit in Memory per GPU.{" "}
                            <Info label="Ryzen AI Max memory on Linux" text={`Linux gives the GPU no dedicated memory: it maps system RAM through the GTT, limited by default to about half the RAM (${Math.round(ram / 2)} GB here). AMD recommends a small BIOS reservation (0.5 GB) and raising the limit with amd-ttm --set <GB> (pipx install amd-debug-tools), then a reboot; its example sets 100 GB on a 128 GB machine. On Windows, with ${gpu.vram} GB reserved, llama.cpp (Vulkan) can also use the shared part, about ${Math.round(gpu.vram + (ram - gpu.vram) / 2)} GB in all, but AMD notes it runs fastest within the reserved ${gpu.vram} GB.`} />
                          </p>
                        );
                      })()}
                      {hwWarnings.map((w) => (
                        <p key={w.short} className="vram-note vram-warn">
                          {w.short} <Info label={w.short} text={w.detail} />
                          {w.fix && (
                            <>
                              {" "}
                              <button type="button" className="vram-share"
                                onClick={() => { interacted.current = true; if (w.fix!.kind === "kv") setKvFormat(w.fix!.to); else setWeightFormat(w.fix!.to); }}>
                                {w.fix.label}
                              </button>
                            </>
                          )}
                        </p>
                      ))}
                    </div>
                  </section>

                  <details className="vram-block">
                    <summary className="vram-block-title vram-summary">
                      {training ? "Overhead" : `Serving engine · ${runtime.label}`}
                    </summary>
                    <div className="vram-field-stack">
                      <div className="vram-field-grid vram-grid-3">
                        {!training && (
                          <Field id="runtime" label="Engine">
                            <select id="runtime" className="vram-select" value={runtimeId} onChange={(e) => { engineChosen.current = true; onRuntime(e.target.value); }}>
                              {RUNTIMES.map((rt) => (
                                <option key={rt.id} value={rt.id} disabled={!runsEngine(gpu, rt.id)}>
                                  {rt.label}{runsEngine(gpu, rt.id) ? "" : " (not on this GPU)"}
                                </option>
                              ))}
                            </select>
                          </Field>
                        )}
                        <Field id="util" label={`Usable memory (%)${utilUser || !engineTarget ? "" : " · auto"}`}
                          info="Share of each GPU the engine takes. Auto follows the engine on this GPU: vLLM gpu_memory_utilization 0.90, SGLang's own mem_fraction_static formula (from the GPU's memory and tensor parallel size), llama.cpp all free memory. Type a value to override it.">
                          <NumberInput id="util" used={shownUtil} value={utilUser ? utilization : String(shownUtil)} onChange={setUtilization} step={0.1} />
                        </Field>
                        <Field id="overhead" label={`Overhead / GPU (GiB)${ohUser || !engineTarget ? "" : " · auto"}`}
                          info="What the engine keeps besides weights and KV cache: CUDA or ROCm context, NCCL, CUDA graphs, activations. Auto uses medians of real server logs for this GPU family (InferenceX); llama.cpp: its 1 GiB --fit margin plus its compute buffer. Type a value to override it.">
                          <NumberInput id="overhead" used={shownOverhead} value={ohUser ? overhead : String(shownOverhead)} onChange={setOverhead} step={0.5} />
                        </Field>
                      </div>
                      {!training && <p className="vram-note">{runtime.note}</p>}
                    </div>
                  </details>
                </div>

                <div className="vram-output-column">
                  <TierHead n={2} title="Your answer" desc={training ? "GPUs and memory for this training run." : "How many GPUs, how fast, and what it costs."} />
                  <section className="vram-results-panel vram-answer" id="vram-answer" tabIndex={-1}>
                    <div className="vram-block-header">
                      <h3 className="vram-block-title">{training ? "To train it you need" : "To run it you need"}</h3>
                      <span className={`vram-status ${statusClass}`}>{statusLabel}</span>
                    </div>
                    <p className="vram-answer-main" ref={watchAnswer}>{headline}</p>
                    <p className="vram-note">
                      {training
                        ? `${MODE_LABEL[mode]} of ${labelOf(model)}, batch of ${nSeqText} × ${tokens(ctx)} tokens per GPU.`
                        : `${labelOf(model)}, ${weightLabel ?? "BF16"} weights, ${nSeqText} concurrent request${nSeq === 1 ? "" : "s"} of ${tokens(ctx)} tokens.`}
                      {!training && splitText && ` Split as ${splitText}.`}
                      {` ${(r.gpus * nVram).toLocaleString("en-US")} GB of GPU memory in total.`}
                    </p>
                    {/* llama.cpp sans -c : « --fit » (activé par défaut) baisse le contexte jusqu'à ce que tout tienne sur
                        le GPU, 4 096 tokens au moins (llama.cpp, tools/fit-params). */}
                    {!training && runtimeId === "llamacpp" && r.status === "oom" && r.maxContext >= 4096 && (
                      <p className="vram-note">
                        {consoleGpu
                          ? <>PS5LM Plus picks the context by itself: about <b>{tokens(r.maxContext)} tokens</b> per request fit on this console.</>
                          : <>Without -c, llama.cpp lowers the context by itself (--fit, on by default) to about <b>{tokens(r.maxContext)} tokens</b> per request to stay on the GPU{withRam ? ", or keeps yours with system RAM below" : ""}.</>}
                      </p>
                    )}
                    {withRam && offload?.plan && (
                      <button type="button" className="vram-cheaper" onClick={() => jumpTo("vram-offload")}>
                        Runs with llama.cpp and {ramText} GB of RAM: <strong>about {tps(offload.plan.tokensPerSecond)} tokens/s</strong>
                        {" "}({gib(offload.plan.vramGiB)} on the GPU{offloadGpus > 1 ? "s" : ""}, {gib(offload.plan.ramGiB)} in RAM)
                        <ChevronRight size={16} aria-hidden="true" />
                      </button>
                    )}
                    {cost && (
                      <>
                        <p className="vram-cost">
                          From <strong>{cost}</strong> to rent, on {offers[0].name.split(" (")[0]}
                          <Info label="rental price" text="On-demand prices, checked every hour. Prices change: check before you rent." />
                          {gpu && PRICED_GPUS.includes(gpu) && (
                            <a className="vram-link vram-cost-link" href="#vram-prices" onClick={(e) => { e.preventDefault(); jumpTo("vram-prices"); }}>
                              {offers.length > 1 ? `Compare ${offers.length} providers` : "Price history"} ↓
                            </a>
                          )}
                        </p>
                      </>
                    )}
                    {cheaper && (
                      <button type="button" className="vram-cheaper" onClick={() => { onGpu(cheaper.g.id); setGpuCount("auto"); }}>
                        {r.status === "oom" ? "Cheapest that fits" : "Cheapest"}: {cheaper.n} × {gpuName0(cheaper.g)}
                        {" · "}<strong>{perHour(cheaper.cost, cheaper.n)}</strong>{cheaper.provider ? ` on ${cheaper.provider}` : ""}
                        <ChevronRight size={16} aria-hidden="true" />
                      </button>
                    )}
                    {training && consoleGpu && (
                      <p className="vram-note vram-warn">Training does not run on a PlayStation 5: PS5LM Plus only runs llama.cpp, for inference.</p>
                    )}
                    {!training && r.status !== "oom" && !offers.length && (
                      <p className="vram-note">
                        {consoleGpu ? "Nobody rents out PlayStation 5s: this one runs on your own console."
                          : gpu ? `No on-demand rental price for this GPU in our sources${providerNames ? ` (${providerNames})` : ""}.` : "Pick a GPU from the list to see its rental price and speed."}
                      </p>
                    )}
                    {cost && pricesStale && (
                      <p className="vram-note vram-warn">Prices were last checked on {pricesStamp}: they may be out of date.</p>
                    )}
                    {r.status === "oom" && autoGpus !== null && (
                      <p className={withRam ? "vram-note" : "vram-note vram-warn"}>
                        {withRam ? `In VRAM alone, ${weightLabel} weights need ${autoGpus} of these GPUs.`
                          : training ? `Not enough: this training run needs ${autoGpus} of these GPUs.`
                            : `Not enough: with ${weightLabel} weights this needs ${autoGpus} of these GPUs.`}
                      </p>
                    )}
                    {betterQuant && (
                      <button type="button" className="vram-option active" onClick={() => setWeightFormat(betterQuant.f.id)}>
                        <span>Quantize to {formatLabel(betterQuant.f.id)}<small>{quality(betterQuant.f.id, betterQuant.f.bits)}</small></span>
                        <b>Fits on {target}</b>
                      </button>
                    )}
                    {r.status !== "oom" && autoGpus !== null && autoGpus < r.gpus && (
                      <p className="vram-note">{autoGpus} would be enough.</p>
                    )}
                    {/* Le conseil suit le mode : pas de « essayez LoRA » à qui est déjà en LoRA, rien pour l'entraînement sur
                        console (impossible, dit plus haut). */}
                    {neverFits && !consoleGpu && <p className="vram-note">More GPUs do not help: {neverFitsWhy}.</p>}
                    {autoGpus === null && !(training && consoleGpu) && (
                      <p className="vram-note vram-warn">
                        {mode === "full" ? "Try LoRA or QLoRA, a shorter sequence or a smaller batch."
                          : mode === "lora" ? "Try QLoRA, a shorter sequence or a smaller batch."
                            : training ? "Try a shorter sequence or a smaller batch."
                              : "Reduce the context or the number of requests, or pick a smaller weight format."}
                      </p>
                    )}
                  </section>

                  {offloadAsked && offload && gpu && (
                    <section className="vram-results-panel vram-offload" id="vram-offload" tabIndex={-1} aria-labelledby="vram-offload-title">
                      <h3 className="vram-block-title vram-panel-title" id="vram-offload-title">
                        {r.status === "oom" ? "Not enough VRAM? Use your system RAM" : `Or ${offloadGpus} × ${gpuShort} plus system RAM`}
                      </h3>
                      <div className="vram-field-grid vram-grid-2">
                        <Field id="ram" label="System RAM (GB)">
                          <NumberInput id="ram" used={nRam} value={ramGb} onChange={setRamGb} step={16} />
                        </Field>
                        <Field id="ramt" label="Memory type" info="With part of the model in RAM, generation speed depends on the RAM bandwidth: channels × speed.">
                          <select id="ramt" className="vram-select" value={ramType.id} onChange={(e) => setRamTypeId(e.target.value)}>
                            {RAM_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                        </Field>
                      </div>
                      {offload.plan ? (
                        <>
                          <p className="vram-offload-answer">
                            Fits with llama.cpp: <strong>{gib(offload.plan.vramGiB)}</strong> on the GPU{offloadGpus > 1 ? "s" : ""},{" "}
                            <strong>{gib(offload.plan.ramGiB)}</strong> in RAM, about <strong>{tps(offload.plan.tokensPerSecond)} tokens/s</strong> for one request.
                          </p>
                          <p className="vram-note">
                            {offload.plan.kind === "experts"
                              ? `The experts of ${offload.plan.cpuLayers} of the ${offload.plan.layers} layers stay in RAM (${offload.plan.flag}). Attention, shared weights and the KV cache stay on the GPU, so each token only reads its active experts from RAM.`
                              : `${offload.plan.layers - offload.plan.cpuLayers} of the ${offload.plan.layers} layers run on the GPU (${offload.plan.flag}), the others in RAM.`}
                          </p>
                          <div className="vram-code-wrap">
                            <pre className="vram-code" tabIndex={0} aria-label="llama.cpp command with RAM offload"><code>{offloadCommand}</code></pre>
                          </div>
                        </>
                      ) : offload.ramShort ? (
                        <p className="vram-note vram-warn">Not even with {ramText} GB of RAM: {gib(offload.ramShort)} missing. Pick a smaller weight format or a shorter context.</p>
                      ) : (
                        <p className="vram-note">
                          With llama.cpp it fits on {offloadGpus} × {gpuShort} without using RAM.{" "}
                          {runtime.id !== "llamacpp" && <button type="button" className="vram-share" onClick={() => onRuntime("llamacpp")}>Switch to llama.cpp</button>}
                        </p>
                      )}
                      <p className="vram-footnote">
                        Rough estimate (±50%). Each token reads the GPU part at {gpu.bandwidth} TB/s and the RAM part at about half of{" "}
                        {ramType.bandwidth} GB/s, minus {RAM_RESERVED_GIB} GB kept for the system. Prompt processing is also slower than on a GPU alone.
                      </p>
                    </section>
                  )}

                  {/* Strata (moteur pour PC de jeu, cache d'experts) : chiffres publiés par le projet, pas estimés ici. */}
                  {strata && strataFast && strataSlow && gpu && (
                    <section className="vram-results-panel" aria-labelledby="vram-strata-title">
                      <h3 className="vram-block-title vram-panel-title" id="vram-strata-title">Or run it with Strata</h3>
                      <p className="vram-note">
                        Strata, an open-source engine for gaming PCs, runs it. {strataHow(strata)}
                        {" "}{gpu.vram < strata.minVramGB
                          ? `It needs ${strata.minVramGB} GB of VRAM or more: your ${gpuShort} has ${gpu.vram} GB.`
                          : strataPick(strata, strataMemory(strata, nRam, gpu.vram))
                            ? `With ${ramText} GB of RAM${strata.memory === "combined" ? ` and ${gpu.vram} GB of VRAM` : ""}, the project recommends ${strataPick(strata, strataMemory(strata, nRam, gpu.vram))!.pick}.`
                            : strata.memory === "combined"
                              ? `It needs at least ${Math.min(...strata.ram.map((x) => x.gb))} GB of RAM and VRAM together.`
                              : `It needs ${Math.min(...strata.ram.map((x) => x.gb))} GB of RAM or more.`}
                      </p>
                      <p className="vram-note">
                        Measured by the project on an {strataFast.setup}: {strataWrite(strataFast)} tokens/s ({strataFast.size})
                        {strataSlow !== strataFast ? ` to ${strataWrite(strataSlow)} tokens/s (${strataSlow.size})` : ""}.
                      </p>
                      <p className="vram-note">
                        <Link className="vram-link" to={`${modelPath(model)}#strata`}>Requirements and speeds by RAM</Link>
                        {" · "}
                        <a className="vram-link" href={`https://github.com/${strata.repo}`} target="_blank" rel="noopener noreferrer">
                          Strata on GitHub<span className="sr-only"> (opens in a new tab)</span>
                        </a>
                      </p>
                      <p className="vram-footnote">
                        Figures published by the Strata project (read on {strata.checked}), not estimated by this calculator.
                      </p>
                    </section>
                  )}

                  {speed && gpu && (
                    <section className="vram-results-panel">
                      <h3 className="vram-block-title">Speed &amp; cost</h3>
                      <div className="vram-metrics-grid vram-metrics-pair">
                        <Metric label="Per request" value={`≈ ${tps(speed.perRequest)} tok/s`} info="Generation speed seen by one user, at full context" />
                        <Metric label={`Total, ${nSeqText} request${nSeq === 1 ? "" : "s"}`} value={`≈ ${tps(speed.total)} tok/s`} />
                        {ttft !== null && (
                          <Metric label={`First token, ${tokens(ctx)} prompt`} value={`≈ ${secs(ttft)}`}
                            info="Time to read a prompt as long as the chosen context before the first token appears (prefill), for one request. It depends on the GPU's compute (TFLOPS), not its memory bandwidth." />
                        )}
                        {economics && (
                          <Metric primary label="Your cost per 1M tokens" value={`$${usd(economics.selfPer1M)}`}
                            info={`Generated tokens at full load: ${r.gpus.toLocaleString("en-US")} × ${gpuShort} on ${offers[0].name.split(" (")[0]}`} />
                        )}
                      </div>
                      {/* Le même modèle par API : un seul prix (le moins cher des deux sources) et le seuil de
                          rentabilité calculé dessus. La simulation face à l'API la plus rapide : panneau suivant. */}
                      {api ? (
                        <div className="vram-api">
                          <p className="vram-note">
                            Same model through an API: <b>${usd(api.output)}</b> per 1M output tokens on {api.provider}{" "}
                            <Info label="API price" text={`Cheapest of ${api.offers} API offer${api.offers === 1 ? "" : "s"} (OpenRouter and Hugging Face Inference Providers): $${api.input} per 1M input tokens, $${api.output} per 1M output tokens.`} />
                          </p>
                          {economics && (economics.utilizationToBreakEven <= 1 ? (
                            <p className="vram-note">
                              Self-hosting beats it once your GPUs are generating more than <b>{Math.max(1, Math.round(economics.utilizationToBreakEven * 100))}%</b> of
                              the time, about {bigCount(economics.breakEvenTokensPerDay)} tokens a day. Below that, the API is cheaper.
                            </p>
                          ) : (
                            <p className="vram-note">
                              The API is cheaper even at full load. To change that: more concurrent requests, a smaller weight format, or cheaper GPUs.
                            </p>
                          ))}
                          {entry?.apis && (
                            <Link className="vram-link" to={`${modelPath(model)}#api-providers`}>
                              Compare {entry.apis.providers.length} API provider{entry.apis.providers.length === 1 ? "" : "s"}: price, speed, first token
                            </Link>
                          )}
                        </div>
                      ) : economics && apiKey ? (
                        <p className="vram-note">No API offer for this model on OpenRouter or Hugging Face.</p>
                      ) : null}
                      <p className="vram-footnote">
                        Estimate (±30–50%, no speculative decoding): generation at full context from memory bandwidth ({gpu.bandwidth} TB/s per GPU)
                        and {runtime.label} efficiency, first token from {layerSplit ? "llama.cpp's measured speed on this GPU" : `compute (${gpu.tflops} TFLOPS)`}.
                        API prices and speeds: OpenRouter and Hugging Face Inference Providers.
                      </p>
                    </section>
                  )}

                  <section className="vram-results-panel">
                    <h3 className="vram-block-title">VRAM per GPU</h3>
                    <div className="vram-bar" role="img" aria-label={`${gib(r.totalGiB)} needed of ${gib(r.budgetGiB)} usable`}>
                      {segments.map((s) => (
                        <span key={s.key} className={`vram-seg seg-${s.key}`} style={{ width: `${(s.value / scale) * 100}%` }} />
                      ))}
                      {r.totalGiB > r.budgetGiB && (
                        <span className="vram-bar-limit" style={{ left: `${(r.budgetGiB / scale) * 100}%` }} />
                      )}
                    </div>
                    <ul className="vram-legend">
                      {segments.map((s) => (
                        <li key={s.key}><i className={`seg-${s.key}`} />{s.label}<b>{gib(s.value)}</b></li>
                      ))}
                    </ul>
                    <div className="vram-metrics-grid vram-metrics-3">
                      <Metric primary label="Needed" value={gib(r.totalGiB)} />
                      <Metric primary label="Usable" value={gib(r.budgetGiB)} info="Memory per GPU × usable %" />
                      <Metric label={r.freeGiB >= 0 ? "Headroom" : "Missing"} value={gib(Math.abs(r.freeGiB))} />
                    </div>
                    {/* Méthode et précision : dans la FAQ (le panneau « How it is computed » a été retiré). */}
                    <p className="vram-footnote">
                      {training ? "Activations are a ±30% estimate. " : catalog.accuracy ? `Median gap against ${catalog.accuracy.runs} real launches: ${catalog.accuracy.weightsPct}% on weights. ` : ""}
                      <a className="vram-link" href={training ? "#faq-training" : "#faq-method"} onClick={openFaq}>How is this computed?</a>
                    </p>
                  </section>



                </div>
              </div>
              {/* Sous les deux colonnes, sur toute la largeur : la démonstration de vitesse, la capacité et les mesures réelles.
                  Dans la colonne de droite, elles l'allongeaient de 1 000 px de plus que les réglages (15 octobre 2026). */}
              {!training && (
                <div className="vram-console-more">
                {/* La réponse s'écrit à la vitesse estimée, face à l'API la plus rapide de ce modèle quand elle est connue.
                    A remplacé « Compare with your real usage » (journal collé), le 15 octobre 2026. */}
                {speed && gpu && !training && (() => {
                  const fast = entry?.apis?.providers.filter((p) => p.tps).sort((a, b) => b.tps! - a.tps!)[0];
                  const lanes = [
                    { label: `${r.gpus.toLocaleString("en-US")} × ${gpuShort}`, tps: speed.perRequest, ttft: prefillSeconds(model, 1024, gpu, inputs.tp * inputs.pp, layerSplit, inputs.pp) },
                    ...(fast ? [{ label: `${fast.name} API`, tps: fast.tps!, ttft: (fast.ttftMs ?? 0) / 1000 }] : []),
                  ];
                  const answer = (l: (typeof lanes)[number]) => secs(l.ttft + 500 / Math.max(0.1, l.tps));
                  return (
                    <section className="vram-results-panel vram-watch" aria-labelledby="vram-watch-title">
                      <h3 className="vram-block-title" id="vram-watch-title">Watch it generate</h3>
                      <p className="vram-note">
                        One answer as a user sees it, after a 1K-token prompt{nSeq > 1 ? `, while ${(nSeq - 1).toLocaleString("en-US")} other request${nSeq === 2 ? " runs" : "s run"} at the same time` : ""}.
                        {" "}You read about {Math.round(READING_TPS)} tokens a second
                        {speed.perRequest < READING_TPS ? ": this setup writes slower than you read." : "."}
                      </p>
                      <SpeedPreview key={`${model.id}|${gpuId}|${Math.round(speed.perRequest)}|${fast?.name ?? ""}`} lanes={lanes} />
                      <p className="vram-note">
                        A 500-token answer takes about <b>{answer(lanes[0])}</b> here{lanes[1] ? <>, <b>{answer(lanes[1])}</b> with {fast!.name}</> : null}.
                      </p>
                      <p className="vram-footnote">
                        Simulated in your browser from the estimate above (the text is only filler).
                        {fast ? " API speed and first token: measured by Hugging Face Inference Providers." : ""} Reading speed: 238 words a minute (Brysbaert, 2019).
                      </p>
                    </section>
                  );
                })()}

                {!training && (
                  <section className="vram-results-panel vram-capacity">
                    {/* GPU seuls : sous un « Fits with RAM », 0 requête et « None » se contredisaient. */}
                    <h3 className="vram-block-title">{withRam ? "Capacity in VRAM alone" : "Capacity"}</h3>
                    <div className="vram-metrics-grid">
                      <Metric label={`Max requests at ${tokens(ctx)}`} value={r.maxSequences.toLocaleString("en-US")} />
                      <Metric
                        label={`Max context with ${nSeqText} request${nSeq === 1 ? "" : "s"}`}
                        value={r.maxContext >= model.maxContext ? `${tokens(model.maxContext)} (model max)` : r.maxContext > 0 ? tokens(r.maxContext) : "Does not fit"}
                      />
                      <Metric label="KV per token" value={bytes(kvBytesPerToken(model, ctx, kvBytes))} info="Averaged over the chosen context, whole model. Falls as context grows on sliding-window and compressed layers." />
                      <Metric label="KV per request" value={bytes(kvPerSeq)} info="At the chosen context, whole model, before tensor-parallel sharding" />
                    </div>
                    {withRam && (
                      <p className="vram-note">
                        GPU memory only. With {ramText} GB of system RAM, llama.cpp keeps part of the weights in RAM and runs it: see the answer above.
                      </p>
                    )}
                    {allFullKv > kvPerSeq * 1.5 && (
                      <p className="vram-note">
                        Treating every layer as full attention would give {bytes(allFullKv)} per request,{" "}
                        {(allFullKv / kvPerSeq).toFixed(1)}× more than this architecture actually caches.
                      </p>
                    )}
                    {model.stateBytesPerSeq ? (
                      <p className="vram-note">
                        Plus {bytes(model.stateBytesPerSeq)} of recurrent state per request, fixed whatever the context.
                      </p>
                    ) : null}
                  </section>
                )}

                {ixRuns.length > 0 && (
                  <section className="vram-results-panel" aria-labelledby="vram-real-title">
                    <h3 className="vram-block-title" id="vram-real-title">Estimate vs last seen real</h3>
                    {ixMatch && ixRows.length > 0 ? (
                      <>
                        <div className="vram-table-wrap">
                          <table className="vram-table vram-log-table">
                            <caption className="sr-only">Our estimate against the last real run of this setup</caption>
                            <thead>
                              <tr><th scope="col">{ixMatch.gpus} × {ixGpu(ixMatch.gpu)}, {ixEngine(ixMatch.engine)}</th><th scope="col">Real</th><th scope="col">Estimate</th><th scope="col">Gap</th></tr>
                            </thead>
                            <tbody>
                              {ixRows.map((row) => (
                                <tr key={row.key}>
                                  <th scope="row">{row.label}</th>
                                  <td>{row.real}</td>
                                  <td>{row.ours}</td>
                                  <td className={row.cmp ? `vram-gap ${row.cmp.verdict}` : undefined}>{row.gap}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="vram-footnote">
                          Real: the last run of this exact setup by{" "}
                          <a className="vram-link" href="https://inferencex.semianalysis.com/inference" target="_blank" rel="noopener noreferrer">InferenceX<span className="sr-only"> (opens in a new tab)</span></a>{" "}
                          (SemiAnalysis, Apache 2.0), {longDay(ixMatch.day)}, {ixEngine(ixMatch.engine)}{ixMatch.version ? ` ${ixMatch.version}` : ""}, 1K-token prompts and answers
                          {ixMatch.url && <>, <a className="vram-link" href={ixMatch.url} target="_blank" rel="noopener noreferrer">run log<span className="sr-only"> (opens in a new tab)</span></a></>}.
                          The estimate covers every setup; real runs exist for a few.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="vram-note">
                          InferenceX measured {labelOf(model)} on real servers. Pick one of these setups to see our estimate next to the real run:
                        </p>
                        <div className="vram-real-choices">
                          {ixChoices.map((x) => (
                            <button key={x.id} type="button" className="vram-share" onClick={() => applyRun(x)}>
                              {x.gpus} × {ixGpu(x.gpu)}, {ixEngine(x.engine)}, {formatLabel(x.format).split(" · ")[0]}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </section>
                )}
                </div>
              )}
              {hasOptions && (
                <section className="vram-tier">
                  <TierHead n={3} title={optionsTier.title} desc={optionsTier.desc} />
                  {matrix.length > 0 && matrixCtx.length > 1 && (
                    <section className="vram-results-panel vram-fit-panel">
                      <h3 className="vram-block-title vram-panel-title" id="vram-fit-title">Does it fit? Weights × context on {r.gpus.toLocaleString("en-US")} × {gpuShort}</h3>
                      <div className="vram-fit-body">
                      <div className="vram-table-wrap">
                        <table className="vram-table vram-fit" aria-labelledby="vram-fit-title">
                          <thead>
                            <tr><th scope="col">Weights</th>{matrixCtx.map((c) => <th scope="col" key={c}>{tokens(c)}</th>)}</tr>
                          </thead>
                          <tbody>
                            {matrix.map(({ f, cells }) => (
                              <tr key={f.id}>
                                <th scope="row">{formatLabel(f.id)}</th>
                                {cells.map((st, j) => (
                                  <td key={matrixCtx[j]}>
                                    <button
                                      type="button" className={`vram-fit-cell ${st}`} aria-pressed={f.id === wf && matrixCtx[j] === ctx}
                                      onClick={() => { setWeightFormat(f.id); setContext(String(matrixCtx[j])); }}
                                    >
                                      <span aria-hidden="true">{st === "oom" ? "✕" : st === "tight" ? "~" : st === "ram" ? "◐" : "✓"}</span>
                                      <span className="sr-only">{`${formatLabel(f.id)} at ${tokens(matrixCtx[j])} tokens: ${STATUS_LABEL[st]}`}</span>
                                    </button>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="vram-fit-aside">
                        <p className="vram-fit-aside-title">Longest context that fits</p>
                        <ul>
                          {fitGroups.map((g) => (
                            <li key={g.from}><span>{g.from === g.to ? g.from : `${g.from} to ${g.to}`}</span> <b>{g.text}</b></li>
                          ))}
                        </ul>
                      </div>
                      </div>
                      {/* Mobile : une ligne par format à la place de la grille, plus large que l'écran. */}
                      <ul className="vram-options vram-fit-list">
                        {fitMax.map(({ f, j, text }) => (
                          <li key={f.id}>
                            <button
                              type="button" className={`vram-option${f.id === wf ? " active" : ""}`} aria-pressed={f.id === wf}
                              onClick={() => { setWeightFormat(f.id); if (j >= 0) setContext(String(matrixCtx[j])); }}
                            >
                              <span>{formatLabel(f.id)}</span>
                              <b className={j < 0 ? "vram-fit-none" : undefined}>{text}</b>
                            </button>
                          </li>
                        ))}
                      </ul>
                      <p className="vram-footnote">
                        <span className="vram-fit-desktop">✓ fits · ~ tight · {gridRam && `◐ fits with ${ramText} GB of system RAM (slower) · `}✕ does not fit. </span>
                        Context per request, {nSeqText} concurrent request{nSeq === 1 ? "" : "s"}, {runtime.label}.
                        <span className="vram-fit-desktop"> Click a cell to apply it.</span>
                        <span className="vram-fit-mobile"> Tap a format to use it at its longest context.</span>
                      </p>
                    </section>
                  )}
                  <div className="vram-results-row">
                    {quantRows.length > 1 && (
                      <section className={`vram-results-panel${listsOpen.quant ? " open" : ""}`}>
                        <h3 className="vram-block-title vram-panel-title">Quantization on {gpuName.split(" · ")[0]}</h3>
                        {quantGroups.map(({ n, rows }) => (
                          <div key={n ?? "over"} className={`vram-quant-group${rows.every(({ f }) => mobileExtra(quantOrder.indexOf(f.id), f.id === wf)) ? " vram-extra" : ""}`}>
                            <h4 className="vram-quant-count">{n === null ? "Over 128 GPUs" : `${n} GPU${n > 1 ? "s" : ""}`}</h4>
                            <ul className="vram-options vram-options-grid">
                              {rows.map(({ f, size }) => (
                                <li key={f.id} className={mobileExtra(quantOrder.indexOf(f.id), f.id === wf)}>
                                  <button type="button" className={`vram-option${f.id === wf ? " active" : ""}`} aria-pressed={f.id === wf} onClick={() => setWeightFormat(f.id)}>
                                    <span>{formatLabel(f.id)}<small>{quality(f.id, f.bits)} · {gib(size / GiB)}</small></span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        <ShowAll count={quantRows.length} open={listsOpen.quant} onOpen={() => openList("quant")} />
                        <p className="vram-footnote">Fewer bits per weight, fewer GPUs, at some quality cost. The KV cache is set separately.</p>
                      </section>
                    )}

                    {options.length > 0 && (
                      <section className={`vram-results-panel${listsOpen.cheapest ? " open" : ""}`}>
                        <h3 className="vram-block-title vram-panel-title">Cheapest GPUs that fit in one server</h3>
                        <ul className="vram-options">
                          {options.map(({ g, n, cost: c, provider }, i) => (
                            <li key={g.id} className={mobileExtra(i, g.id === gpuId)}>
                              <button
                                type="button" className={`vram-option${g.id === gpuId ? " active" : ""}`} aria-pressed={g.id === gpuId}
                                onClick={() => { onGpu(g.id); setGpuCount("auto"); }}
                              >
                                <span>{g.label}<small>{n} GPU{n > 1 ? "s" : ""}{provider ? ` · ${provider}` : ""}</small></span>
                                <b>{Number.isFinite(c) ? perHour(c, n) : "No rental price"}</b>
                              </button>
                            </li>
                          ))}
                        </ul>
                        <ShowAll count={options.length} open={listsOpen.cheapest} onOpen={() => openList("cheapest")} />
                        <p className="vram-footnote">Cheapest on-demand price among {providerNames}, checked every hour{hasPrices ? "" : ` (last check ${pricesStamp})`}.{training ? "" : " Weight format as selected above."}</p>
                      </section>
                    )}

                    {otherModels.length > 0 && (
                      <section className={`vram-results-panel${listsOpen.models ? " open" : ""}`}>
                        <h3 className="vram-block-title vram-panel-title">Other models that fit on {r.gpus.toLocaleString("en-US")} × {gpuShort}</h3>
                        <ul className="vram-options">
                          {otherModels.map(({ m, f, lab }, i) => (
                            <li key={m.id} className={mobileExtra(i, false)}>
                              <button type="button" className="vram-option" onClick={() => { interacted.current = true; onModel(m.id); setWeightFormat(f.id); jumpTo("vram-calculator"); }}>
                                <span><Flag country={lab?.country} />{modelName(m)}<small>{m.params < 10 ? m.params.toFixed(1) : m.params.toFixed(0)}B{m.activeParams ? `, ${m.activeParams}B active` : ""}</small></span>
                                <b>{f.id === "native" ? m.native!.label.replace(" (native)", "") : f.label.split(" · ")[0].replace("GGUF ", "")}</b>
                              </button>
                            </li>
                          ))}
                        </ul>
                        <ShowAll count={otherModels.length} open={listsOpen.models} onOpen={() => openList("models")} />
                        <p className="vram-footnote">Largest first, each with the most faithful weight format that fits {tokens(ctx)} tokens × {nSeqText} request{nSeq === 1 ? "" : "s"}.</p>
                      </section>
                    )}
                  </div>
                </section>
              )}

              {(hasPrices || !training) && (
                <section className="vram-tier">
                  <TierHead n={hasOptions ? 4 : 3} title={rentTier.title} desc={rentTier.desc} />
                  {hasPrices && (
                    <section className="vram-results-panel vram-prices" id="vram-prices" tabIndex={-1}>
                      <h3 className="vram-block-title vram-panel-title" id="vram-prices-title">{gpuShort} rental prices</h3>
                      {offers.length > 0
                        ? <PriceTable offers={offers} n={priceGpus} labelledBy="vram-prices-title" />
                        : <p className="vram-note">No on-demand offer for this GPU today in our sources ({providerNames}).</p>}
                      <div className="vram-prices-history"><PriceHistory gpu={gpu} /></div>
                      <p className="vram-footnote">
                        On-demand, per GPU, checked every hour (last check {pricesStamp}). Monthly = {HOURS_PER_MONTH} hours.{" "}
                        <Link className="vram-link" to={gpuPricePath(gpu)}>{gpuShort} price page</Link>
                      </p>
                    </section>
                  )}

                  {gpu && homePower && (
                    <HomePower gpu={gpu} gpus={r.gpus} power={homePower}
                      rent={costUsd !== null ? { name: offers[0].name.split(" (")[0], price: costUsd } : null}
                      tokPerSec={!training && speed ? speed.total : null} />
                  )}

                  {!training && (
                    <section className="vram-results-panel vram-deploy">
                      <div className="vram-block-header">
                        <h3 className="vram-block-title vram-panel-title">Deploy it</h3>
                        <div className="vram-tabs" role="group" aria-label="Inference engine">
                          {DEPLOY_ENGINES.filter((e) => runsEngine(gpu, e.id)).map((e) => (
                            <button key={e.id} type="button" aria-pressed={deployTab === e.id}
                              className={`vram-tab${deployTab === e.id ? " active" : ""}`} onClick={() => setDeployEngine(e.id)}>
                              {consoleGpu ? "PS5LM Plus" : e.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="vram-code-wrap">
                        {/* Focusable : sur mobile la commande défile à l'horizontale, le clavier doit pouvoir la parcourir. */}
                        <pre className="vram-code" tabIndex={0} aria-label="Deploy command"><code>{command}</code></pre>
                        <button type="button" className="vram-share vram-code-copy" onClick={() => {
                          navigator.clipboard.writeText(command.split("\n").filter((l) => !l.startsWith("#")).join("\n")).then(() => {
                            setCmdCopied(true); setAnnounce("Command copied to the clipboard."); setTimeout(() => setCmdCopied(false), 2000);
                          }, () => {});
                        }}>{cmdCopied ? "Copied" : "Copy"}</button>
                      </div>
                      {offers.length > 0 && r.status !== "oom" && (
                        <div className="vram-rent">
                          {offers.slice(0, 1).map((o) => (
                            <a key={o.id} className="vram-rent-btn" href={rentUrl(o)} target="_blank" rel={rentRel(o)}>
                              Rent {r.gpus.toLocaleString("en-US")} × {gpuShort} on {o.name.split(" (")[0]} <b>{perHour(r.gpus * o.price, r.gpus)}</b>
                              <span className="sr-only"> (opens in a new tab)</span>
                            </a>
                          ))}
                        </div>
                      )}
                      {hasReferral && (
                        <p className="vram-footnote">Some rental links are affiliate links: StudioTV may earn a commission, at no extra cost to you.</p>
                      )}
                    </section>
                  )}

                  <aside className="vram-promo" aria-label="GPU rental prices">
                    <div>
                      <p className="vram-promo-eyebrow">GPU rental prices</p>
                      <p className="vram-promo-title">Compare {PRICED_GPUS.length} GPUs across {Object.keys(prices.providers).length} providers</p>
                      <p className="vram-promo-text">The cheapest on-demand price for every card, its 30-day average and daily price history. Checked every hour.</p>
                    </div>
                    <Link className="vram-cta vram-promo-cta" to="/gpu-prices">See all GPU prices →</Link>
                    {promoGpus.length > 0 && (
                      <ul className="vram-promo-gpus">
                        {promoGpus.map(({ g, price }) => (
                          <li key={g.id}>
                            <Link to={gpuPricePath(g)}>{gpuName0(g).replace(/^(NVIDIA|AMD|GeForce) /, "")}<b>{priceUsd(price)}/h</b></Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </aside>
                </section>
              )}
            </div>
            {/* La réponse sort de l'écran pendant qu'on règle les champs (mobile : elle est dessous ; ordinateur :
                le champ GPU est plus bas qu'elle). Cette barre la rappelle en bas de l'écran tant que le
                calculateur est visible (position: sticky), et s'efface quand la réponse est à l'écran. */}
            <p className="sr-only" role="status" aria-atomic="true">{announce}</p>
            <a
              href="#vram-answer" className={`vram-sticky${answerInView ? " vram-sticky-off" : ""}`}
              aria-hidden={answerInView || undefined} tabIndex={answerInView ? -1 : undefined}
              onClick={(e) => { e.preventDefault(); jumpTo("vram-answer"); }}
            >
              {/* Nom lu par les lecteurs d'écran = le texte visible, plus la ponctuation et la
                  destination en texte masqué (le libellé doit contenir ce qui est affiché). */}
              <span className={`vram-status ${statusClass}`}>{statusLabel}</span>
              <span className="sr-only">: </span>
              <strong>{headline.split(" · ")[0]}</strong>
              {cost && <><span className="sr-only">, </span><span className="vram-sticky-cost">{cost}</span></>}
              <span className="sr-only">. Go to the answer</span>
            </a>
          </div>
        </section>
        </ErrorBoundary>

        <section className="vram-content" id="vram-guide">
          {/* Nouveautés, tenues à jour par la veille (scripts/watch-models.ts, catalog.json) : une carte,
              trois onglets (tendances Hugging Face, popularité sur Ollama, dernières sorties). Toute la
              ligne charge le modèle dans le calculateur ci-dessus. Deux colonnes sur grand écran ;
              5 lignes puis « Show more » sur mobile, où la page est déjà longue. */}
          <article className="vram-content-card vram-news" id="whats-new">
            <h2>New &amp; trending open models</h2>
            {newsTabs.length > 1 && (
              <div className="vram-news-tabs" role="tablist" aria-label="New and trending models" onKeyDown={onNewsKey}>
                {newsTabs.map((t) => (
                  <button
                    key={t} type="button" role="tab" id={`news-tab-${t}`} className="vram-news-tab"
                    aria-selected={newsTab === t} aria-controls={`news-panel-${t}`} tabIndex={newsTab === t ? 0 : -1}
                    onClick={() => { setNewsTab(t); setNewsMore(false); }}
                  >
                    {t === "trending" ? "Trending" : t === "ollama" ? "Popular on Ollama" : "Latest releases"}
                  </button>
                ))}
              </div>
            )}
            {newsTabs.includes("trending") && (
              <div role="tabpanel" id="news-panel-trending" aria-labelledby="news-tab-trending" hidden={newsTab !== "trending"}>
                <p>The most popular open models on Hugging Face this week. Pick one to see what it takes to run it.</p>
                <ol className={`vram-news-list${newsMore ? " more" : ""}`}>
                  {trendRows.slice(0, 10).map(({ key, m, preset, name }, i) => {
                    const lab = labOfOrg(m.lab ?? "", catalog);
                    return (
                      <NewsRow
                        key={key} name={name} lab={lab} rank={i + 1}
                        onClick={() => (preset ? quickPick(preset.id) : sizeRepo(m.repo ?? ""))}
                        meta={[
                          m.params && `${m.params < 10 ? m.params.toFixed(1) : Math.round(m.params).toLocaleString("en-US")}B · ${q4GiB(m.params)} GiB in 4-bit`,
                          lab?.name ?? m.lab, m.parent && `fine-tune of ${tidyLabel(prettyModelName(m.parent.split("/")[1]), m.parent)}`, m.hf && `${compact(m.hf.likes)} likes`,
                        ].filter(Boolean).join(" · ")}
                      />
                    );
                  })}
                </ol>
                <p className="vram-footnote">From Hugging Face, refreshed every hour. Community re-quantizations are left out.</p>
              </div>
            )}
            {newsTabs.includes("ollama") && (
              <div role="tabpanel" id="news-panel-ollama" aria-labelledby="news-tab-ollama" hidden={newsTab !== "ollama"}>
                <p>What people run on their own machines: the most downloaded models in the Ollama library.</p>
                <ol className={`vram-news-list${newsMore ? " more" : ""}`}>
                  {ollamaRows.slice(0, 10).map(({ key, m, preset, name }, i) => {
                    const o = m.ollama!;
                    // Le calculateur sait le charger (preset, ou dépôt Hugging Face lisible) ; sinon sa page Ollama.
                    const loadable = preset || (m.repo && m.sizable !== false);
                    return (
                      <NewsRow
                        key={key} name={name} lab={m.lab ? labOfOrg(m.lab, catalog) : undefined} rank={i + 1}
                        {...(loadable
                          ? { onClick: () => (preset ? quickPick(preset.id) : sizeRepo(m.repo!)) }
                          : { href: `https://ollama.com/library/${o.name}` })}
                        meta={[o.sizes?.length ? o.sizes.join(" · ") : undefined, `${compact(o.pulls)} pulls`, ollamaCommand(o)].filter(Boolean).join(" · ")}
                      />
                    );
                  })}
                </ol>
                <p className="vram-footnote">From ollama.com, refreshed daily. Pulls count the whole model family, all sizes together.</p>
              </div>
            )}
            <div
              role={newsTabs.length > 1 ? "tabpanel" : undefined} id="news-panel-latest"
              aria-labelledby={newsTabs.length > 1 ? "news-tab-latest" : undefined} hidden={newsTab !== "latest" && newsTabs.length > 1}
            >
              <p>New open models are added automatically, usually within the hour of their release.</p>
              <ol className={`vram-news-list${newsMore ? " more" : ""}`}>
                {LATEST.map((m) => {
                  const lab = labOf(m, catalog);
                  return (
                    <NewsRow
                      key={m.id} onClick={() => quickPick(m.id)} lab={lab} name={modelName(m)} isNew={isNewModel(m, pricesDate)}
                      meta={`${m.params < 10 ? m.params.toFixed(1) : m.params.toFixed(0)}B${m.activeParams ? ` (${m.activeParams}B active)` : ""} · ` +
                        `${shortDate(m.released!)}${lab ? ` · ${lab.name}` : ""}`}
                    />
                  );
                })}
              </ol>
              <p className="vram-footnote"><a className="vram-link" href="/llm-releases.xml">Follow new releases (RSS)</a></p>
            </div>
            <button type="button" className="vram-share vram-news-more" aria-expanded={newsMore} onClick={() => setNewsMore((v) => !v)}>
              {newsMore ? "Show less" : "Show more"}
            </button>
            <p className="vram-footnote">
              Lab countries from the registry of this site, completed with{" "}
              <a className="vram-link" href="https://epoch.ai/data/ai-models" target="_blank" rel="noopener noreferrer">
                Epoch AI<span className="sr-only"> (opens in a new tab)</span>
              </a>{" "}(CC BY 4.0).
            </p>
          </article>

          {/* Les mêmes chiffres hors du site : serveur MCP sur /api/mcp (scripts/api-server.ts ; /mcp est la
              page qui explique le MCP), fichiers JSON et
              badges générés au build (seo/publicApi.ts). */}
          <article className="vram-content-card" id="api">
            <h2>Use it in Claude, Cursor or ChatGPT</h2>
            <p>
              Add this calculator to your AI assistant once, then just ask, for example: <q>Can Qwen3.8 27B run on one RTX 4090 with a 32K
              context?</q> It answers with the same numbers as this page, for the listed models and any Hugging Face model, with GPU
              rental prices. No key, no sign-up.
            </p>
            {(() => {
              const c = MCP_CLIENTS.find((x) => x.id === mcpClient) ?? MCP_CLIENTS[0];
              return (
                <>
                  <div className="vram-tabs vram-mcp-tabs" role="group" aria-label="Your AI assistant">
                    {MCP_CLIENTS.map((x) => (
                      <button key={x.id} type="button" aria-pressed={x.id === c.id} className={`vram-tab${x.id === c.id ? " active" : ""}`}
                        onClick={() => { setMcpClient(x.id); setMcpCopied(false); }}>
                        {x.label}
                      </button>
                    ))}
                  </div>
                  <p className="vram-note vram-mcp-how">{c.how}</p>
                  {c.install && (
                    <p><a className="vram-share vram-mcp-install" href={c.install.href}>{c.install.label}</a></p>
                  )}
                  <div className="vram-code-wrap">
                    <pre className="vram-code" tabIndex={0} aria-label={`${c.label} setup`}><code>{c.code}</code></pre>
                    <button type="button" className="vram-share vram-code-copy" onClick={() => {
                      navigator.clipboard.writeText(c.code).then(() => {
                        setMcpCopied(true); setAnnounce("Copied to the clipboard."); setTimeout(() => setMcpCopied(false), 2000);
                      }, () => {});
                    }}>{mcpCopied ? "Copied" : "Copy"}</button>
                  </div>
                </>
              );
            })()}
            <p>
              The same data as JSON: <a className="vram-link" href="/api/models.json">/api/models.json</a> (every model, with the link to its
              file), <a className="vram-link" href="/api/gpus.json">/api/gpus.json</a>, and one file per model such as{" "}
              <a className="vram-link" href="/api/models/llama-3-3-70b.json">/api/models/llama-3-3-70b.json</a>. Each model page also has a
              VRAM badge to paste in a model card.
            </p>
          </article>

          <article className="vram-content-card" id="models">
            <h2 id="models-title">VRAM requirements by model</h2>
            <p>
              GPU memory for the weights alone, in GiB. Each model page details the KV cache by context length
              and how many GPUs of each type it takes.
            </p>
            <div className="vram-table-wrap">
              <table className="vram-table" aria-labelledby="models-title">
                <thead>
                  <tr>
                    <th scope="col">Model</th>
                    <th scope="col">Parameters</th>
                    <th scope="col">BF16</th>
                    <th scope="col">FP8</th>
                    <th scope="col">4-bit</th>
                  </tr>
                </thead>
                {modelGroups.map((g) => (
                  <tbody key={g}>
                    <tr className="vram-table-group"><th scope="colgroup" colSpan={5}>{g}</th></tr>
                    {MODEL_TABLE.filter(({ m }) => m.group === g).map(({ m, lab, bf16, fp8, q4 }) => (
                      <tr key={m.id}>
                        <th scope="row">
                          <Flag country={lab?.country} label={lab?.country && `${lab.name}, ${countryName(lab.country)}`} />
                          <Link to={modelPath(m)}>{modelName(m)}<span className="sr-only"> VRAM</span></Link>
                          {isNewModel(m, pricesDate) && <span className="vram-new">New</span>}
                        </th>
                        <td>{m.params < 10 ? m.params.toFixed(1) : Math.round(m.params).toLocaleString("en-US")}B{m.activeParams ? <span className="vram-table-dim"> ({m.activeParams}B active)</span> : null}</td>
                        <td>{fmt(bf16)}</td>
                        <td>{fmt(fp8)}</td>
                        <td>{fmt(q4)}</td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          </article>

          {/* Accordéon : les questions seules prennent peu de place ; les réponses repliées
              restent dans le HTML (lues par Google et les assistants IA, et par le JSON-LD). */}
          <div className="vram-faq" id="faq">
            <h2>LLM VRAM FAQ</h2>
            <div className="vram-faq-list">
              {FAQ.map(({ q, a, points, id }) => (
                <details key={q} id={id} className="vram-faq-row">
                  <summary><h3>{q}</h3></summary>
                  <div className="vram-faq-answer">
                    <p>{a}</p>
                    {points && (
                      <ul>
                        {points.map(([b, t]) => <li key={b}><b>{b}</b> {t}</li>)}
                      </ul>
                    )}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
